// generate-ai-diets.component.ts
/*
 * NOTE: To enable native PDF saving and immediate opening on Android & iOS,
 * this code requires the following Capacitor plugins.
 * If you haven't already, please run:
 *
 * npm install @capacitor/core @capacitor/filesystem @capacitor-community/file-opener
 * npx cap sync
 */

import {
  ChangeDetectorRef,
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  HostListener,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { Location } from '@angular/common';
import {
  ToastController,
  AlertController,
  IonContent,
  ActionSheetController,
  MenuController,
  Platform,
} from '@ionic/angular/lazy';
import { environment } from '../../../../environments/environment';
import { ApiService } from 'src/app/core/services/api.service';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { JwtService } from 'src/app/core/services/jwt.service';
import { jsPDF } from 'jspdf';

import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';

export interface ChatMessage {
  id: string | number;
  question_id?: number;
  role: 'user' | 'ai';
  content: string;
  timestamp: Date;
  isDietJson?: boolean;
  dietData?: any;
  isRecipeJson?: boolean;
  recipeData?: any;
  isExerciseJson?: boolean;
  exerciseData?: any;
  response_type?: string;
  userPrompt?: string;
  feedbackStatus?: 'none' | 'submitted';
  isStreaming?: boolean;
  suggestedPrompts?: string[];
  isValid?: boolean;
  errorMessage?: string;
  itemRef?: string;
  regenUsed?: number;
}

export interface ChatSession {
  session_id: string | number;
  title: string;
  is_pinned: boolean | number;
  created_date?: string;
  last_message_date?: string;
}

export interface SuggestionOption {
  title: string;
  prompt: string;
  value: number;
  icon?: string;
}

export interface RecipeMacro {
  label: string;
  value: string | number;
  unit: string;
  cls: string;
}

export interface ExerciseDay {
  label: string;
  warmup: string[];
  workout: string[];
  cooldown: string[];
}

export interface AiLimitInfo {
  daily_limit?: number | null;
  daily_used?: number;
  daily_remaining?: number | null;
  regen_limit?: number | null;
  regen_used?: number;
  regen_remaining?: number | null;
  can_generate?: boolean;
  can_regenerate?: boolean;
}

// Interface for the custom calendar grid
export interface CalendarDay {
  date: Date | null;
  isHighlighted?: boolean;
  isSelected?: boolean;
  isToday?: boolean;
  isDisabled?: boolean;
}

@Component({
  selector: 'app-generate-ai-diets',
  templateUrl: './generate-ai-diets.component.html',
  styleUrls: ['./generate-ai-diets.component.scss'],
  standalone: false,
})
export class GenerateAiDietComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chatContent', { static: false }) chatContent!: IonContent;

  sessions: ChatSession[] = [];
  currentSessionId: string | number | null = null;
  aiApiSessionId: string | null = null;
  currentRefId: string | null = null;
  currentMessages: ChatMessage[] = [];
  chatInput: string = '';
  today: Date = new Date();


  isTyping: boolean = false;
  isLoadingHistory: boolean = false;
  isLoadingMessages: boolean = false;
  formJson: any = null;

  searchQuery: string = '';
  showScrollButton: boolean = false;
  isUserScrolledUp: boolean = false;
  private scrollTimeout: any;

  private suppressAccordionAutoScroll: boolean = false;
  private accordionSuppressTimeout: any;

  private resizeObserver: ResizeObserver | null = null;
  private observedNodes: Element[] = [];

  private suppressResizeAutoScroll: boolean = false;

  private abortController: AbortController | null = null;
  private activeAlert: any = null;
  public onboardingFormJson: any = null;

  // ─── Profile summary state ──────────────────────────────────────────
  public profileSummary: string | null = null;
  public profileSummaryRaw: string | null = null;
  private profileSummaryLoading: boolean = false;
  private profileSummaryClientId: number | null = null;

  // API key sent as a URL query param (?apiKey=...) — mirrors the PHP
  // `$_GET['apiKey']` lookup used by the summary endpoint.
  private readonly SUMMARY_API_KEY: string = 'cb06d8f2-b03c-4ccd-9857-ba2680496ed9';
  // ─────────────────────────────────────────────────────────────────────

  isOnboardingPending: boolean = false;
  onboardingFormURL: string = '';

  loadingPhrases: string[] = [];
  currentLoadingPhrase: string = '';
  private loadingPhraseInterval: any;

  selectedCategory: 'diet' | 'recipes' | 'exercise' | null = 'diet';

  dailyLimitMessage: string = '';

  aiLimits: { diet: AiLimitInfo; recipe: AiLimitInfo; exercise: AiLimitInfo } = {
    diet:     { daily_limit: null, daily_used: 0, daily_remaining: null, regen_limit: 3, regen_used: 0, regen_remaining: 3, can_generate: true, can_regenerate: true },
    recipe:   { daily_limit: null, daily_used: 0, daily_remaining: null, regen_limit: 3, regen_used: 0, regen_remaining: 3, can_generate: true, can_regenerate: true },
    exercise: { daily_limit: null, daily_used: 0, daily_remaining: null, regen_limit: 3, regen_used: 0, regen_remaining: 3, can_generate: true, can_regenerate: true },
  };

  private readonly EXERCISE_SINGLE_DAY_CONSTRAINT =
    '\n\nIMPORTANT: Provide a SINGLE-DAY (one-session) workout plan only. ' +
    'Do NOT return a multi-day split, weekly schedule, or `days` array. ' +
    'The JSON must have `warmup`, `workout`, and `cooldown` as arrays ' +
    'directly on the top-level data object.';

  /**
   * Cutoff hour (24h local time) after which generated plans default to tomorrow.
   * Requirement: 5:00 PM.
   */
  private readonly NEXT_DAY_CUTOFF_HOUR: number = 17;

  /**
   * How many months of the calendar the user may navigate in either direction.
   * Guards against infinite past/future navigation.
   */
  private readonly CALENDAR_NAVIGATION_MONTH_WINDOW: number = 12;

  // ─── Custom Date Picker State ───────────────────────────────────────
  isDatePickerModalOpen: boolean = false;
  isFetchingDates: boolean = false;
  isSavingDate: boolean = false;
  datePickerLoadFailed: boolean = false;
  occupiedDates: Set<string> = new Set();
  highlightedDates: any[] = [];
  selectedDateForSave: string = '';
  pendingSaveData: any = null;
  pendingSaveType: 'diet' | 'exercise' | null = null;

  // Custom Calendar properties
  currentMonthDate: Date = new Date();
  calendarDays: CalendarDay[] = [];
  currentMonthName: string = '';
  currentYear: number = 0;
  // ──────────────────────────────────────────────────────────────────

  dietSuggestions: SuggestionOption[] = [
    { title: 'Get My Diet Plan', prompt: 'Suggest a personalized 1500 calorie Indian diet plan for weight loss.', value: 1, icon: 'restaurant-outline' },
    { title: 'Healthy Recipes', prompt: 'Share some quick and healthy Indian recipes for breakfast.', value: 2, icon: 'nutrition-outline' },
    { title: 'Diet for My Goal', prompt: 'Suggest a high-protein vegetarian meal plan for muscle gain.', value: 3, icon: 'leaf-outline' },
    { title: 'Nutrition Tips', prompt: 'Give me 7 simple nutrition tips for better health.', value: 4, icon: 'sparkles-outline' }
  ];

  recipeSuggestions: SuggestionOption[] = [
    { title: 'North Indian Cuisine', prompt: 'Give me a healthy North Indian Dal Makhani recipe.', value: 1, icon: 'flame-outline' },
    { title: 'South Indian Cuisine', prompt: 'Share an authentic low-calorie Oats Masala Dosa recipe.', value: 2, icon: 'cafe-outline' },
    { title: 'Asian Cuisine', prompt: 'Provide a healthy Asian Tofu Vegetable Stir-Fry recipe.', value: 3, icon: 'fast-food-outline' },
    { title: 'Mediterranean Cuisine', prompt: 'Suggest an easy Mediterranean Quinoa Salad recipe.', value: 4, icon: 'pizza-outline' }
  ];

  exerciseSuggestions: SuggestionOption[] = [
    { title: 'Fat-Burning Session', prompt: 'Design a 30-minute high-intensity fat-burning workout routine for a single session.', value: 1, icon: 'bicycle-outline' },
    { title: 'Strength Session', prompt: 'Suggest a full-body strength training routine for a single session.', value: 2, icon: 'barbell-outline' },
    { title: 'Core & Mobility', prompt: 'Give me a 15-minute core strengthening routine for one session.', value: 3, icon: 'body-outline' },
    { title: 'Home Workout', prompt: 'Provide a quick no-equipment home exercise routine for a single beginner session.', value: 4, icon: 'fitness-outline' }
  ];

  private rawHttp: HttpClient;
  private cdr = inject(ChangeDetectorRef);
  private clientIdFromJwt: number | null = null;

  constructor(
    private router: Router,
    private httpBackend: HttpBackend,
    private alertController: AlertController,
    private actionSheetCtrl: ActionSheetController,
    private toaster: NotificationService,
    private apiService: ApiService,
    private location: Location,
    private menuCtrl: MenuController,
    private jwtService: JwtService,
    private platform: Platform
  ) {
    this.rawHttp = new HttpClient(this.httpBackend);
  }

  ngOnInit() {
    this.applyTabFromUrl();
    this.extractClientIdFromJwt();
    this.loadSessionHistory(true);
  }

  ngAfterViewInit() {
    // Defer so the messages container exists before we observe it.
    setTimeout(() => this.attachResizeObserver(), 150);
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.scrollTimeout) { clearTimeout(this.scrollTimeout); }
    if (this.loadingPhraseInterval) { clearInterval(this.loadingPhraseInterval); }
    if (this.accordionSuppressTimeout) { clearTimeout(this.accordionSuppressTimeout); }
  }

  ionViewWillEnter() {
    this.checkOnboardingStatus();
    this.applyTabFromUrl();
    this.loadSessionHistory(false);
  }

  goBack() { this.location.back(); }

  checkOnboardingStatus(): void {
    this.apiService.post('dynamicForms/check_onboarding_form', { form_type: 'onboarding' }).subscribe(async (response: any) => {
      if (response?.isValid && response.data?.encoded_form_number) {
        const clientId = btoa(response.data.data.client_id);
        this.onboardingFormURL = `/forms/${response.data.encoded_form_number}?client_id=${clientId}`;
        const formFilledData = response.data.data.form_filled;
        this.isOnboardingPending = !formFilledData || formFilledData === '' || formFilledData === null;

        if (this.isOnboardingPending) {
          this.showOnboardingBlocker();
        } else {
          this.onboardingFormJson = formFilledData;
          // Form is filled → fetch (or refresh) the profile summary.
          // NOTE: we intentionally do not early-return when a fetch is in
          // flight, so re-entering the view after editing the onboarding form
          // picks up the new data. If a fetch is already running we let it
          // complete and then re-run.
          this.ensureProfileSummary(response.data.data.client_id, true);
        }
      }
    });
  }

  // ─── Profile summary flow ───────────────────────────────────────────

  /**
   * Fetches (or generates) the client profile summary via
   * `Summaries/generate_client_profile_summary_with_ai`.
   *
   * The backend reads `apiKey` from the URL query string
   * (equivalent to PHP's `$_GET['apiKey']`), so we append it here.
   *
   * @param force If true, ignore the in-flight guard and re-fetch.
   */
  private ensureProfileSummary(clientId: number, force: boolean = false): void {
    if (clientId === null || clientId === undefined) return;

    if (this.profileSummaryLoading && !force) return;
    if (this.profileSummaryLoading && force) {
      // Let the in-flight request finish, then re-run once.
      setTimeout(() => this.ensureProfileSummary(clientId, false), 500);
      return;
    }

    this.profileSummaryClientId = clientId;
    this.profileSummaryLoading = true;

    const endpoint =
      'Summaries/generate_client_profile_summary_with_ai' +
      `?apiKey=${encodeURIComponent(this.SUMMARY_API_KEY)}`;

    this.apiService.post(endpoint, { client_id: clientId }).subscribe({
      next: (res: any) => {
        this.profileSummaryLoading = false;
        const summary = this.extractProfileSummaryText(res);
        if (summary) {
          this.storeProfileSummary(summary);
          this.cdr.detectChanges();
        } else {
          console.warn('[profile_summary] summary endpoint returned no usable string.');
        }
      },
      error: (err) => {
        this.profileSummaryLoading = false;
        console.warn('[profile_summary] fetch failed:', err);
      },
    });
  }

  private extractProfileSummaryText(res: any): string | null {
    if (!res) return null;
    if (!res.isValid && res.isValid !== undefined) return null;

    const candidates: any[] = [
      res?.data?.summary,
      res?.data?.data?.summary,
      typeof res?.data === 'string' ? res.data : null,
      res?.summary,
      typeof res === 'string' ? res : null,
    ];

    for (const c of candidates) {
      if (typeof c === 'string' && c.trim().length > 0) {
        return c.trim();
      }
    }
    return null;
  }

  private cleanProfileSummary(raw: string): string {
    if (!raw) return '';
    let s = raw.replace(/\r\n/g, '\n');
    s = s.replace(/\*\*(.*?)\*\*/g, '$1');
    s = s.replace(/__(.*?)__/g, '$1');
    s = s.replace(/(^|\s)\*(?!\s)(.*?)(?<!\s)\*(?=\s|$|[.,;:!?])/g, '$1$2');
    s = s.replace(/(^|\s)_(?!\s)(.*?)(?<!\s)_(?=\s|$|[.,;:!?])/g, '$1$2');
    s = s.replace(/[ \t]+/g, ' ');
    s = s.replace(/\n{3,}/g, '\n\n');
    return s.trim();
  }

  private storeProfileSummary(rawSummary: string): void {
    if (!rawSummary) return;
    this.profileSummaryRaw = rawSummary;
    const cleaned = this.cleanProfileSummary(rawSummary);
    this.profileSummary = cleaned.length > 0 ? cleaned : rawSummary;
    console.log('[profile_summary] stored (len=' + this.profileSummary.length + ')');
  }

  public get profileSummaryForPayload(): string | null {
    if (!this.profileSummary) return null;
    const trimmed = this.profileSummary.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  // ─────────────────────────────────────────────────────────────────────

  async showOnboardingBlocker() {
    const alert = await this.alertController.create({
      header: 'Action Required',
      message: 'Please complete Onboarding Form before generating AI plans. This helps the AI provide accurate and personalized recommendations.',
      backdropDismiss: false,
      keyboardClose: false,
      buttons: [
        {
          text: 'Go Back',
          role: 'cancel',
          handler: () => {
            this.location.back();
          }
        },
        {
          text: 'Complete Now',
          handler: () => {
            this.router.navigateByUrl(this.onboardingFormURL);
          }
        }
      ]
    });
    await alert.present();
  }

  private applyTabFromUrl(): void {
    try {
      let tab: string | null = null;
      const routerUrl = this.router?.url || '';
      const qIndex = routerUrl.indexOf('?');
      if (qIndex >= 0) {
        const params = new URLSearchParams(routerUrl.substring(qIndex + 1));
        tab = params.get('tab');
      }
      if (!tab && typeof window !== 'undefined' && window.location?.search) {
        const params = new URLSearchParams(window.location.search);
        tab = params.get('tab');
      }
      if (!tab) return;
      const normalized = tab.toLowerCase().trim();
      if (normalized === 'diet' || normalized === 'recipes' || normalized === 'exercise') {
        this.selectedCategory = normalized as 'diet' | 'recipes' | 'exercise';
        this.updateDailyLimitMessage();
        this.cdr.detectChanges();
      }
    } catch { /* silently ignore malformed URLs */ }
  }

  selectCategory(category: 'diet' | 'recipes' | 'exercise') {
    this.selectedCategory = category;
    this.updateDailyLimitMessage();
    this.cdr.detectChanges();
  }

  get currentSuggestions(): SuggestionOption[] {
    switch (this.selectedCategory) {
      case 'recipes': return this.recipeSuggestions;
      case 'exercise': return this.exerciseSuggestions;
      case 'diet': return this.dietSuggestions;
      default: return [];
    }
  }

  private getAiTabParam(): 'diet' | 'recipe' | 'exercise' | 'chat' {
    switch (this.selectedCategory) {
      case 'recipes':  return 'recipe';
      case 'exercise': return 'exercise';
      case 'diet':     return 'diet';
      default:         return 'chat';
    }
  }

  isDailyLimitReached(): boolean {
    const key = this.getAiTabParam();
    if (key === 'chat') return false;
    const lim = this.aiLimits[key];
    if (!lim || lim.daily_remaining === null || lim.daily_remaining === undefined) return false;
    return lim.daily_remaining <= 0;
  }

  private updateDailyLimitMessage() {
    const key = this.getAiTabParam();
    if (key === 'chat') { this.dailyLimitMessage = ''; return; }
    const lim = this.aiLimits[key];
    if (!lim || lim.daily_remaining === null || lim.daily_remaining === undefined) {
      this.dailyLimitMessage = '';
      return;
    }
    if (lim.daily_remaining <= 0) {
      this.dailyLimitMessage = `You have reached your daily limit of ${lim.daily_limit} ${key} generations. Please try again tomorrow.`;
    } else {
      this.dailyLimitMessage = '';
    }
  }

  private applyLimitsFromResponse(limits: any, tabParamOverride?: 'diet' | 'recipe' | 'exercise' | 'chat') {
    if (!limits) return;
    const key = tabParamOverride || this.getAiTabParam();
    if (key === 'chat') return;
    if (key === 'diet' || key === 'recipe' || key === 'exercise') {
      this.aiLimits[key] = { ...this.aiLimits[key], ...limits };
      this.updateDailyLimitMessage();
      if (limits.daily_remaining === 1) {
        this.toaster.presentToast(`You have 1 daily generation left for ${key}.`, '', 'warning');
      }
      if (limits.regen_remaining === 1) {
        this.toaster.presentToast(`You have 1 regeneration left for this item.`, '', 'warning');
      }
    }
  }

  regenerationsLeftFor(msg: ChatMessage): number | null {
    if (!msg || msg.role !== 'ai') return null;
    const limit = this.aiLimitsForTabName(this.getAiTabParam()).regen_limit ?? 3;
    const used = msg.regenUsed ?? 0;
    return Math.max(0, limit - used);
  }

  private aiLimitsForTabName(key: 'diet' | 'recipe' | 'exercise' | 'chat'): AiLimitInfo {
    if (key === 'chat') {
      return { daily_limit: null, daily_used: 0, daily_remaining: null, regen_limit: null, regen_used: 0, regen_remaining: null };
    }
    return this.aiLimits[key] || { daily_limit: null, daily_used: 0, daily_remaining: null, regen_limit: 3, regen_used: 0, regen_remaining: 3 };
  }

  isRegenerateDisabled(msg: ChatMessage): boolean {
    const left = this.regenerationsLeftFor(msg);
    if (left === null) return false;
    return left <= 0;
  }

  getRecipeMacros(nutrition: any): RecipeMacro[] {
    if (!nutrition) return [];
    const normalize = (val: any): string | number => {
      if (val === undefined || val === null || val === '') return '—';
      if (typeof val === 'string') {
        const cleaned = val.replace(/[^\d.]/g, '');
        return cleaned === '' ? val : cleaned;
      }
      return val;
    };
    const pills: RecipeMacro[] = [
      { label: 'Calories', value: normalize(nutrition.calories ?? nutrition.energy), unit: 'kcal', cls: 'calories' },
      { label: 'Carbs', value: normalize(nutrition.carbs ?? nutrition.carbohydrates), unit: 'g', cls: 'carbs' },
      { label: 'Protein', value: normalize(nutrition.protein), unit: 'g', cls: 'protein' },
      { label: 'Fat', value: normalize(nutrition.fat ?? nutrition.total_fat), unit: 'g', cls: 'fat' },
      { label: 'Fibre', value: normalize(nutrition.fibre ?? nutrition.fiber ?? nutrition.dietary_fiber), unit: 'g', cls: 'fibre' },
    ];
    return pills.filter((p) => p.value !== '—' && p.value !== '');
  }

  private getRecipeDescription(title: string): string {
    const t = (title || '').toLowerCase().trim();
    const isBeverage = /(chai|tea|coffee|juice|smoothie|shake|drink|lassi|buttermilk|water|cooler|mocktail|brew|latte)/i.test(t);
    const isSnack = /(snack|salad|chaat|finger|bite|starter|appetizer|canap|tikka|kebab|samosa)/i.test(t);
    const isDessert = /(dessert|sweet|kheer|halwa|ladoo|barfi|kulfi|pudding|cake|cookie|gulab|rasmalai|ice cream)/i.test(t);
    const isBreakfast = /(breakfast|poha|upma|paratha|dosa|idli|pancake|omelette|porridge|toast|muesli|granola|chilla)/i.test(t);

    const safeTitle = title || 'recipe';

    if (isBeverage) return `A refreshing and nourishing serving of ${safeTitle}, crafted with wholesome ingredients and balanced for a healthy lifestyle.`;
    if (isSnack) return `A light and satisfying ${safeTitle} that fits perfectly into your daily nutrition goals without compromising on taste.`;
    if (isDessert) return `A guilt-free approach to ${safeTitle}, made healthier with natural ingredients so you can indulge mindfully.`;
    if (isBreakfast) return `A wholesome and energizing ${safeTitle} to start your day with the right balance of nutrients.`;

    return `A delicious and nourishing ${safeTitle}, prepared with carefully selected ingredients to support a balanced and healthy lifestyle.`;
  }

  /**
   * FIX: The LLM (and older saved responses) sometimes returns a
   * variations-shaped recipe instead of the canonical
   * { title, ingredients, instructions, nutrition } shape. This
   * normalizes any reasonable variant into the canonical shape so the
   * template always has `ingredients` and `instructions` arrays to
   * iterate over, and always has a `title`.
   *
   * If the recipe has variations, the first variation is promoted to
   * the primary recipe, and the remaining variations are preserved as
   * lightweight name+description entries.
   */
  private normalizeRecipeData(raw: any): any {
    if (!raw || typeof raw !== 'object') {
      return { title: 'Recipe', ingredients: [], instructions: [], nutrition: {} };
    }

    let data = raw;
    if (data.recipe && typeof data.recipe === 'object') data = data.recipe;
    if (data.data && typeof data.data === 'object' && data.data.title) data = data.data;

    let title: string = data.title || data.name || 'Recipe';
    let ingredients: any = data.ingredients;
    let instructions: any = data.instructions || data.steps || data.method;
    let nutrition: any = data.nutrition || {};
    let variations: any = data.variations;

    // If top-level ingredients are missing but variations exist, promote
    // the first variation.
    if ((!Array.isArray(ingredients) || ingredients.length === 0)
        && Array.isArray(variations) && variations.length > 0) {
      const first = variations[0] || {};
      title = first.name || first.title || title;
      ingredients = first.ingredients || [];
      instructions = first.instructions || first.steps || instructions || [];
    }

    if (!Array.isArray(ingredients)) {
      ingredients = ingredients ? [String(ingredients)] : [];
    }
    if (!Array.isArray(instructions)) {
      instructions = instructions ? [String(instructions)] : [];
    }
    if (!nutrition || typeof nutrition !== 'object') {
      nutrition = {};
    }

    // Downgrade any remaining variations to name+description for the UI.
    let normalizedVariations: any[] = [];
    if (Array.isArray(variations) && variations.length > 0) {
      normalizedVariations = variations
        .slice(1)
        .filter((v: any) => v && typeof v === 'object')
        .map((v: any) => ({
          name: v.name || v.title || '',
          description: v.description || '',
        }));
    }

    const normalized: any = {
      title,
      ingredients: ingredients.map((x: any) => String(x)).filter((s: string) => s.trim().length > 0),
      instructions: instructions.map((x: any) => String(x)).filter((s: string) => s.trim().length > 0),
      nutrition,
    };
    if (normalizedVariations.length > 0) {
      normalized.variations = normalizedVariations;
    }
    return normalized;
  }

  startLoadingPhrases(userQuery: string = '') {
    const query = userQuery.toLowerCase().trim();
    const isGreeting = ['hi', 'hello', 'hey', 'hii', 'hiii', 'helo'].includes(query);
    const isShort = query.length < 10
      && !['diet', 'food', 'meal', 'plan', 'eat', 'hungry', 'recipe', 'workout', 'exercise', 'training'].some(w => query.includes(w));

    const universalPhrases = [
      'Thinking...',
      'Understanding your request...',
      'Analyzing what you need...',
      'Gathering the details...',
      'Working on it...',
      'Processing your request...',
      'Putting things together...',
      'Organizing the information...',
      'Structuring the response...',
      'Refining the details...',
      'Adding the finishing touches...',
      'Almost done...'
    ];

    const greetingPhrases = [
      'Thinking...',
      'Generating response...'
    ];

    this.loadingPhrases = (isGreeting || isShort) ? greetingPhrases : universalPhrases;

    let i = 0;
    this.currentLoadingPhrase = this.loadingPhrases[0];
    this.loadingPhraseInterval = setInterval(() => {
      i = (i + 1) % this.loadingPhrases.length;
      this.currentLoadingPhrase = this.loadingPhrases[i];
      this.cdr.detectChanges();
    }, 2500);
  }

  stopLoadingPhrases() {
    if (this.loadingPhraseInterval) { clearInterval(this.loadingPhraseInterval); this.loadingPhraseInterval = null; }
    this.currentLoadingPhrase = '';
  }

  onScroll(event: any) {
    if (!event || !event.detail) return;
    const { scrollHeight, scrollTop, clientHeight, deltaY } = event.detail;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    if (deltaY < 0) { this.isUserScrolledUp = true; }
    if (distanceFromBottom <= 100) { this.isUserScrolledUp = false; }
    this.showScrollButton = distanceFromBottom > 150;
  }

  private attachResizeObserver() {
    if (typeof ResizeObserver === 'undefined') return;
    const candidates = [
      document.querySelector('.chat-scroll-container'),
      document.querySelector('.messages-container'),
    ].filter(Boolean) as Element[];
    if (candidates.length === 0) return;
    this.resizeObserver?.disconnect();
    this.observedNodes = candidates;
    this.resizeObserver = new ResizeObserver(() => {
      if (this.suppressResizeAutoScroll) return;
      if (this.suppressAccordionAutoScroll) return;
      if (this.isUserScrolledUp) return;
      this.performScrollToBottom(false);
    });
    candidates.forEach((el) => this.resizeObserver!.observe(el));
  }

  private performScrollToBottom(animated: boolean) {
    const messagesContainer = document.querySelector('.messages-container');
    const lastMsg = messagesContainer?.lastElementChild as HTMLElement | null;
    if (lastMsg) {
      try {
        lastMsg.scrollIntoView({ block: 'end', inline: 'nearest', behavior: animated ? 'smooth' : 'auto' });
        return;
      } catch { /* fall through */ }
    }
    const wrapper = document.querySelector('.chat-scroll-container') as HTMLElement | null;
    if (wrapper) {
      try {
        wrapper.scrollIntoView({ block: 'end', inline: 'nearest', behavior: animated ? 'smooth' : 'auto' });
        return;
      } catch { /* fall through */ }
    }
    if (this.chatContent) {
      try { this.chatContent.scrollToBottom(animated ? 300 : 0); } catch { /* noop */ }
    }
  }

  private scrollToBottom(duration: number = 300, force: boolean = false) {
    if (!force && this.isUserScrolledUp) return;
    if (this.scrollTimeout) { clearTimeout(this.scrollTimeout); }
    if (!this.resizeObserver) { this.attachResizeObserver(); }
    const animated = duration > 0;
    this.performScrollToBottom(animated);
    this.scrollTimeout = setTimeout(() => {
      if (!force && this.isUserScrolledUp) return;
      this.performScrollToBottom(animated);
      setTimeout(() => {
        if (!this.isUserScrolledUp) {
          this.performScrollToBottom(false);
          // FIX #24: reset the FAB visibility deterministically once we know
          // we are at the bottom, regardless of nested timeout order.
          this.showScrollButton = false;
        }
      }, 150);
      setTimeout(() => {
        if (!this.isUserScrolledUp) {
          this.performScrollToBottom(false);
          this.showScrollButton = false;
        }
      }, 400);
    }, 50);
  }

  scrollToBottomManually() {
    this.showScrollButton = false;
    this.isUserScrolledUp = false;
    this.scrollToBottom(300, true);
  }

  get hasStreamingContent(): boolean { return this.currentMessages.some((m) => m.isStreaming && m.content && m.content.length > 0); }

  stopGenerating() {
    if (this.abortController) { this.abortController.abort(); this.abortController = null; }
    this.isTyping = false;
    this.stopLoadingPhrases();
    this.currentMessages = this.currentMessages.filter((m) => !m.isStreaming);
    this.cdr.detectChanges();
  }

  /**
   * FIX #15: Editing a user prompt now removes the original user message
   * (and its paired AI response) and prefills the input box. This makes the
   * "edit" action match common chat-app behaviour: the old turn is replaced
   * by the new one the user is about to send.
   *
   * If you'd rather keep the old message visible, replace the splice block
   * below with just the `this.chatInput = ...` line and a highlight class.
   */
  editPrompt(msg: ChatMessage) {
    if (this.isTyping) { this.stopGenerating(); }

    const userIndex = this.currentMessages.findIndex((m) => m.id === msg.id);
    if (userIndex >= 0) {
      // Remove the user message and the immediately-following AI response
      // (if any) that belongs to the same question_id.
      const toRemove = new Set<string | number>();
      toRemove.add(msg.id);
      const qid = msg.question_id;
      if (qid !== undefined && qid !== null) {
        for (let i = userIndex + 1; i < this.currentMessages.length; i++) {
          const m = this.currentMessages[i];
          if (m.role === 'user') break;
          if (m.role === 'ai' && m.question_id === qid) {
            toRemove.add(m.id);
          }
        }
      }
      this.currentMessages = this.currentMessages.filter((m) => !toRemove.has(m.id));
    }

    this.chatInput = msg.content;
    this.cdr.detectChanges();
    this.scrollToBottom(0, true);
  }

  private isGenericStatusMessage(msg: string | undefined | null): boolean {
    if (!msg) return true;
    const t = String(msg).trim().toLowerCase();
    if (t.length === 0) return true;
    return ['success', 'ok', 'done', 'true', 'yes', 'null', 'undefined', 'n/a', 'na'].includes(t);
  }

  private stripMarkdownJson(text: string): string {
    if (!text) return '';
    let s = text.trim();
    const match = s.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
    if (match && match[1]) {
      return match[1].trim();
    }
    return s;
  }

  private buildCopyText(msg: ChatMessage): string {
    if (msg.isRecipeJson && msg.recipeData) {
      const r = msg.recipeData;
      let s = `Recipe: ${r.title || 'Untitled'}\n`;
      if (r.description && String(r.description).trim().length > 0) {
        s += `\n${r.description}\n`;
      }
      const macros = this.getRecipeMacros(r.nutrition || {});
      if (macros.length > 0) {
        s += '\nNutrition:\n';
        macros.forEach((m) => { s += `- ${m.label}: ${m.value} ${m.unit}\n`; });
      }
      if (Array.isArray(r.ingredients) && r.ingredients.length > 0) {
        s += '\nIngredients:\n';
        r.ingredients.forEach((i: string) => { s += `- ${i}\n`; });
      }
      if (Array.isArray(r.instructions) && r.instructions.length > 0) {
        s += '\nInstructions:\n';
        r.instructions.forEach((i: string, idx: number) => { s += `${idx + 1}. ${i}\n`; });
      }
      if (Array.isArray(r.variations) && r.variations.length > 0) {
        s += '\nOther Variations:\n';
        r.variations.forEach((v: any) => {
          s += `- ${v.name}: ${v.description}\n`;
        });
      }
      return s.trim();
    }

    if (msg.isExerciseJson && msg.exerciseData) {
      const e = msg.exerciseData;
      let s = `Exercise Plan: ${e.title || 'Untitled'}\n`;
      if (e.duration) s += `Duration: ${e.duration}\n`;
      const days: ExerciseDay[] = Array.isArray(e.days) ? e.days : [];
      days.forEach((d) => {
        s += '\n';
        if (d.label) s += `${d.label}\n`;
        if (d.warmup && d.warmup.length > 0) {
          s += 'Warm-Up:\n';
          d.warmup.forEach((w) => { s += `- ${w}\n`; });
        }
        if (d.workout && d.workout.length > 0) {
          s += 'Main Workout:\n';
          d.workout.forEach((w) => { s += `- ${w}\n`; });
        }
        if (d.cooldown && d.cooldown.length > 0) {
          s += 'Cool Down:\n';
          d.cooldown.forEach((c) => { s += `- ${c}\n`; });
        }
      });
      return s.trim();
    }

    if (msg.isDietJson && Array.isArray(msg.dietData) && msg.dietData.length > 0) {
      let s = 'Personalized Diet Plan\n';
      msg.dietData.forEach((day: any, idx: number) => {
        const n = day?.day_meal_nutrients || {};
        s += `\nDay ${idx + 1}: Calories ${Math.round(Number(n.energy) || 0)} kcal | Carbs ${Math.round(Number(n.carbs) || 0)}g | Protein ${Math.round(Number(n.protein) || 0)}g | Fat ${Math.round(Number(n.fat) || 0)}g | Fibre ${Math.round(Number(n.fibre) || 0)}g\n`;
        (day?.meal_details || []).forEach((meal: any) => {
          const mn = meal?.meal_nutrients || {};
          s += `\n  ${meal.meal_name} (${Math.round(Number(mn.energy) || 0)} kcal)\n`;
          (meal?.normalizedFoods || []).forEach((f: any) => {
            const qty = f.quantity || 1;
            const unit = f.unit || '';
            const name = f.food_name || f.foodName || 'Item';
            s += `    - ${qty} ${unit} ${name}\n`;
          });
        });
      });
      return s.trim();
    }

    return (msg.content || '').trim();
  }

  async copyToClipboard(msg: ChatMessage) {
    const text = this.buildCopyText(msg);
    if (!text) {
      this.toaster.presentToast('Nothing to copy.', '', 'warning');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      this.toaster.presentToast('Response copied to clipboard!', '', 'success');
    } catch (err) {
      this.toaster.presentToast('Failed to copy text.', '', 'danger');
    }
  }

  /**
   * FIX #18/#19: We no longer splice the original message out before the
   * regeneration succeeds. The original stays in place until the new response
   * is persisted; if the request fails, nothing is lost. A confirm dialog is
   * shown first so the user understands the turn will be replaced.
   */
  async regenerateResponse(msg: ChatMessage) {
    if (this.isTyping) {
      this.toaster.presentToast('Please wait for the current response to finish.', '', 'warning');
      return;
    }

    const originalPrompt = msg.userPrompt;
    if (!originalPrompt) {
      this.toaster.presentToast('Could not find original prompt.', '', 'warning');
      return;
    }

    const left = this.regenerationsLeftFor(msg);
    if (left !== null && left <= 0) {
      this.toaster.presentToast('You have reached the maximum of 3 regenerations for this item.', '', 'warning');
      return;
    }

    const confirmed = await this.confirmRegenerate();
    if (!confirmed) return;

    let replaceResponseId: number | null = null;
    if (typeof msg.id === 'string' && msg.id.startsWith('r_')) {
      const parsed = parseInt(msg.id.substring(2), 10);
      if (!isNaN(parsed)) replaceResponseId = parsed;
    }

    const originalIndex = this.currentMessages.findIndex((m) => m.id === msg.id);
    const questionId = msg.question_id || 0;
    this.isTyping = true;
    this.isUserScrolledUp = false;

    this.generateAIResponse(
      this.currentSessionId!,
      questionId,
      originalPrompt,
      undefined,
      replaceResponseId,
      msg.itemRef,
      msg,
      originalIndex
    );
  }

  private async confirmRegenerate(): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const alert = await this.alertController.create({
        header: 'Regenerate response?',
        message: 'The current response will be replaced by a new one. This cannot be undone.',
        buttons: [
          { text: 'Cancel', role: 'cancel', handler: () => resolve(false) },
          { text: 'Regenerate', handler: () => resolve(true) },
        ],
      });
      await alert.present();
    });
  }

  private async saveAndOpenPdf(doc: jsPDF, fileName: string) {
    const isNative = this.platform.is('capacitor') || this.platform.is('cordova') || (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform());

    if (!isNative) {
      doc.save(fileName);
      return;
    }

    // FIX #35: Detect at runtime whether the native file-opener plugin is
    // actually available. If it isn't (plugin not installed / not synced),
    // don't silently no-op — surface a clear message and still try to save.
    const openerAvailable = (() => {
      try {
        return typeof FileOpener !== 'undefined'
          && FileOpener !== null
          && typeof (FileOpener as any).open === 'function';
      } catch {
        return false;
      }
    })();

    if (!openerAvailable) {
      this.toaster.presentToast(
        'Native file opener is not installed on this device. The PDF will be saved but may not open automatically.',
        '',
        'warning'
      );
      try { doc.save(fileName); } catch { /* noop */ }
      return;
    }

    try {
      const base64Data = doc.output('datauristring').split(',')[1];
      const savedFile = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Cache
      });

      try {
        await FileOpener.open({
          filePath: savedFile.uri,
          contentType: 'application/pdf'
        });
      } catch (openerErr) {
        console.warn('FileOpener failed. Falling back to doc.save().', openerErr);
        this.toaster.presentToast('Could not open the PDF automatically; saving locally instead.', '', 'warning');
        doc.save(fileName);
      }
    } catch (err) {
      console.error('Error saving PDF natively:', err);
      this.toaster.presentToast('Could not save PDF natively; attempting local download.', '', 'warning');
      try { doc.save(fileName); } catch { /* noop */ }
    }
  }

  private loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  }

  private async getLogoDataUrl(): Promise<string | null> {
    try {
      const img = await this.loadImage('assets/brand/prim-green.avif');
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        return canvas.toDataURL('image/png');
      }
      return null;
    } catch (e) {
      console.warn('Could not load logo image for PDF header', e);
      return null;
    }
  }

  private getPdfPalette() {
    return {
      primaryColor: [10, 79, 67] as [number, number, number],
      primaryLight: [232, 240, 238] as [number, number, number],
      secondaryColor: [71, 85, 105] as [number, number, number],
      lightGray: [248, 250, 252] as [number, number, number],
      borderGray: [226, 232, 240] as [number, number, number],
      darkText: [15, 23, 42] as [number, number, number],
      mutedText: [100, 116, 139] as [number, number, number],
      white: [255, 255, 255] as [number, number, number],
      warmBg: [248, 250, 252] as [number, number, number],
      warmBorder: [226, 232, 240] as [number, number, number],
      warmAccent: [10, 79, 67] as [number, number, number],
    };
  }

  private drawBrandedHeader(doc: jsPDF, logoDataUrl: string | null, currentPageY: number): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const palette = this.getPdfPalette();

    doc.setFillColor(palette.primaryColor[0], palette.primaryColor[1], palette.primaryColor[2]);
    doc.rect(0, 0, pageWidth, 4, 'F');

    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'PNG', margin, currentPageY, 30, 17);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(palette.mutedText[0], palette.mutedText[1], palette.mutedText[2]);
      doc.text('Smart Nutrition. Better You.', pageWidth - margin, currentPageY + 8, { align: 'right' });
    } else {
      doc.setTextColor(palette.primaryColor[0], palette.primaryColor[1], palette.primaryColor[2]);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.text('prim', margin, currentPageY + 8);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(palette.mutedText[0], palette.mutedText[1], palette.mutedText[2]);
      doc.text('Smart Nutrition. Better You.', pageWidth - margin, currentPageY + 6, { align: 'right' });
    }

    doc.setDrawColor(palette.borderGray[0], palette.borderGray[1], palette.borderGray[2]);
    doc.setLineWidth(0.5);
    doc.line(margin, currentPageY + 18, pageWidth - margin, currentPageY + 18);

    return currentPageY + 30;
  }

  private drawFooter(doc: jsPDF) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const palette = this.getPdfPalette();
    const pageNo = doc.getNumberOfPages();
    doc.setTextColor(palette.mutedText[0], palette.mutedText[1], palette.mutedText[2]);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Page ${pageNo}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
  }

  private drawMetricsStrip(
    doc: jsPDF,
    y: number,
    metrics: Array<{ label: string; value: string }>
  ): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    const palette = this.getPdfPalette();
    const metricsHeight = 22;

    doc.setFillColor(palette.primaryLight[0], palette.primaryLight[1], palette.primaryLight[2]);
    doc.roundedRect(margin, y, contentWidth, metricsHeight, 4, 4, 'F');

    const colWidth = contentWidth / metrics.length;

    metrics.forEach((metric, idx) => {
      const xPos = margin + (idx * colWidth) + (colWidth / 2);
      doc.setTextColor(palette.primaryColor[0], palette.primaryColor[1], palette.primaryColor[2]);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(metric.label, xPos, y + 9, { align: 'center' });
      doc.setFontSize(11);
      doc.setTextColor(palette.darkText[0], palette.darkText[1], palette.darkText[2]);
      doc.text(metric.value, xPos, y + 16, { align: 'center' });
    });

    doc.setDrawColor(palette.primaryColor[0], palette.primaryColor[1], palette.primaryColor[2]);
    doc.setLineWidth(0.2);
    for (let i = 1; i < metrics.length; i++) {
      const xLine = margin + colWidth * i;
      doc.line(xLine, y + 5, xLine, y + 17);
    }

    return y + metricsHeight + 13;
  }

  async downloadRecipePdf(msg: ChatMessage) {
    if (!msg.recipeData) { this.toaster.presentToast('No recipe data available.', '', 'warning'); return; }
    try {
      this.toaster.presentToast('Generating Recipe PDF...', '', 'success');
      await this.generateRecipePdf(msg.recipeData);
    } catch (e) {
      console.error(e);
      this.toaster.presentToast('Failed to download Recipe PDF.', '', 'danger');
    }
  }

  async downloadExercisePdf(msg: ChatMessage) {
    if (!msg.exerciseData) { this.toaster.presentToast('No exercise data available.', '', 'warning'); return; }
    try {
      this.toaster.presentToast('Generating Exercise PDF...', '', 'success');
      await this.generateExercisePdf(msg.exerciseData);
    } catch (e) {
      console.error(e);
      this.toaster.presentToast('Failed to download Exercise PDF.', '', 'danger');
    }
  }

  private async generateRecipePdf(data: any) {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let y = 20;

    const palette = this.getPdfPalette();
    const { primaryColor, secondaryColor, lightGray, borderGray, darkText, mutedText, white } = palette;

    const logoDataUrl = await this.getLogoDataUrl();
    const drawHeader = (currentPageY: number) => this.drawBrandedHeader(doc, logoDataUrl, currentPageY);
    const drawFooter = () => this.drawFooter(doc);

    const recipeTitle = String(data?.title || 'Healthy Recipe');
    const macros = this.getRecipeMacros(data?.nutrition || {});

    y = drawHeader(15);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('Nutritious & Delicious', margin, y);

    y += 12;

    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(darkText[0], darkText[1], darkText[2]);
    const titleLines = doc.splitTextToSize(recipeTitle, contentWidth);
    doc.text(titleLines, margin, y);
    y += (titleLines.length * 10) + 5;

    const descText = (data?.description && String(data.description).trim().length > 0)
      ? String(data.description)
      : this.getRecipeDescription(recipeTitle);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
    const descLines = doc.splitTextToSize(descText, contentWidth - 40);
    doc.text(descLines, margin, y);
    y += (descLines.length * 5) + 14;

    const leftColWidth = contentWidth * 0.6 - 5;
    const rightColX = margin + contentWidth * 0.6 + 5;
    const rightColWidth = contentWidth * 0.4 - 5;

    const ingredientsYStart = y;

    const nutrientRowHeight = 10;
    const nutritionBoxHeaderHeight = 20;
    const nutritionBoxBottomPad = 6;
    const nutritionBoxHeight = nutritionBoxHeaderHeight
      + Math.max(macros.length, 1) * nutrientRowHeight
      + nutritionBoxBottomPad;

    doc.setFillColor(lightGray[0], lightGray[1], lightGray[2]);
    doc.setDrawColor(borderGray[0], borderGray[1], borderGray[2]);
    doc.setLineWidth(0.5);
    doc.roundedRect(rightColX, ingredientsYStart, rightColWidth, nutritionBoxHeight, 4, 4, 'FD');

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('Nutrition Facts', rightColX + (rightColWidth / 2), ingredientsYStart + 10, { align: 'center' });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(mutedText[0], mutedText[1], mutedText[2]);
    doc.text('Amount per serving', rightColX + (rightColWidth / 2), ingredientsYStart + 15, { align: 'center' });

    if (macros.length === 0) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(mutedText[0], mutedText[1], mutedText[2]);
      doc.text('No nutrition data available.', rightColX + (rightColWidth / 2), ingredientsYStart + 30, { align: 'center' });
    } else {
      let nutY = ingredientsYStart + nutritionBoxHeaderHeight + 8;
      macros.forEach((macro) => {
        doc.setDrawColor(borderGray[0], borderGray[1], borderGray[2]);
        doc.setLineWidth(0.3);
        doc.line(rightColX + 5, nutY - 6, rightColX + rightColWidth - 5, nutY - 6);

        const isEnergy = macro.cls === 'energy';

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(isEnergy ? 12 : 10);
        doc.setTextColor(darkText[0], darkText[1], darkText[2]);
        doc.text(macro.label, rightColX + 8, nutY);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(isEnergy ? 12 : 10);
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        const valStr = `${macro.value} ${macro.unit}`.trim();
        doc.text(valStr, rightColX + rightColWidth - 8, nutY, { align: 'right' });

        nutY += nutrientRowHeight;
      });
    }

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('Ingredients', margin, ingredientsYStart);

    let currentIngY = ingredientsYStart + 10;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(darkText[0], darkText[1], darkText[2]);

    const ingredients: string[] = Array.isArray(data?.ingredients) && data.ingredients.length > 0
      ? data.ingredients.map((i: any) => String(i))
      : ['No ingredients listed.'];

    ingredients.forEach((ing) => {
      if (currentIngY > pageHeight - 40) {
        drawFooter();
        doc.addPage();
        currentIngY = drawHeader(15);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text('Ingredients (Cont.)', margin, currentIngY);
        currentIngY += 10;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkText[0], darkText[1], darkText[2]);
      }
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.circle(margin + 2, currentIngY - 1, 1.2, 'F');
      const lines = doc.splitTextToSize(ing, leftColWidth - 8);
      doc.text(lines, margin + 7, currentIngY);
      currentIngY += lines.length * 5 + 1;
    });

    y = Math.max(currentIngY, ingredientsYStart + nutritionBoxHeight) + 12;

    if (y > pageHeight - 60) {
      drawFooter();
      doc.addPage();
      y = drawHeader(15);
    } else {
      doc.setDrawColor(borderGray[0], borderGray[1], borderGray[2]);
      doc.setLineWidth(0.4);
      doc.line(margin, y, pageWidth - margin, y);
      y += 15;
    }

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('Step-by-Step Instructions', margin, y);
    y += 12;

    const instructions: string[] = Array.isArray(data?.instructions) && data.instructions.length > 0
      ? data.instructions.map((i: any) => String(i))
      : ['No instructions provided.'];

    instructions.forEach((inst, idx) => {
      const textLines = doc.splitTextToSize(inst, contentWidth - 15);
      const stepHeight = Math.max(textLines.length * 5, 12);

      if (y + stepHeight > pageHeight - 30) {
        drawFooter();
        doc.addPage();
        y = drawHeader(15);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.text('Instructions (Cont.)', margin, y);
        y += 12;
      }

      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.circle(margin + 4, y + 2, 4, 'F');
      doc.setTextColor(white[0], white[1], white[2]);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(String(idx + 1), margin + 4, y + 2, { align: 'center', baseline: 'middle' });

      doc.setTextColor(darkText[0], darkText[1], darkText[2]);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(textLines, margin + 14, y + 3.5);

      y += stepHeight + 8;
    });

    drawFooter();

    const safeFileName = recipeTitle.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_') || 'Generated';
    await this.saveAndOpenPdf(doc, `AI_Recipe_${safeFileName}.pdf`);
  }

  private async generateExercisePdf(data: any) {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let y = 20;

    const palette = this.getPdfPalette();
    const { primaryColor, secondaryColor, darkText, white } = palette;

    const logoDataUrl = await this.getLogoDataUrl();
    const drawHeader = (currentPageY: number) => this.drawBrandedHeader(doc, logoDataUrl, currentPageY);
    const drawFooter = () => this.drawFooter(doc);

    const normalized = this.normalizeExerciseDataForUI(data);
    const days: ExerciseDay[] = Array.isArray(normalized.days) ? normalized.days : [];
    const singleDay: ExerciseDay = days[0] || { label: '', warmup: [], workout: [], cooldown: [] };

    y = drawHeader(15);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('Move Well, Feel Great', margin, y);

    y += 12;
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(darkText[0], darkText[1], darkText[2]);
    const titleLines = doc.splitTextToSize(String(data.title || 'Personalized Workout Plan'), contentWidth);
    doc.text(titleLines, margin, y);
    y += (titleLines.length * 10) + 5;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
    const descText = 'A carefully designed workout routine crafted to help you build strength, endurance, and confidence. Follow each phase mindfully and stay consistent for the best results!';
    const descLines = doc.splitTextToSize(descText, contentWidth - 40);
    doc.text(descLines, margin, y);
    y += (descLines.length * 5) + 12;

    const totalExercises =
      (singleDay.warmup?.length || 0) +
      (singleDay.workout?.length || 0) +
      (singleDay.cooldown?.length || 0);

    y = this.drawMetricsStrip(doc, y, [
      { label: 'Total Duration', value: String(data.duration || '30 minutes') },
      { label: 'Total Exercises', value: String(totalExercises || '—') },
      { label: 'Session Type', value: 'Single Session' },
    ]);

    const renderSection = (sectionTitle: string, items: any[], startY: number): number => {
      if (!items || items.length === 0) return startY;
      let currentY = startY;

      if (currentY > pageHeight - 60) {
        drawFooter();
        doc.addPage();
        currentY = drawHeader(15);
      }

      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(margin, currentY - 4, 3, 8, 'F');

      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.text(sectionTitle, margin + 8, currentY + 2);
      currentY += 14;

      items.forEach((item: any, idx: number) => {
        const text = typeof item === 'string' ? item : (item?.text || item?.name || JSON.stringify(item));
        const textLines = doc.splitTextToSize(String(text), contentWidth - 15);
        const stepHeight = Math.max(textLines.length * 5, 12);

        if (currentY + stepHeight > pageHeight - 30) {
          drawFooter();
          doc.addPage();
          currentY = drawHeader(15);
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
          doc.text(sectionTitle + ' (Cont.)', margin + 8, currentY + 2);
          currentY += 14;
        }

        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.circle(margin + 4, currentY + 2, 4, 'F');
        doc.setTextColor(white[0], white[1], white[2]);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(String(idx + 1), margin + 4, currentY + 2, { align: 'center', baseline: 'middle' });

        doc.setTextColor(darkText[0], darkText[1], darkText[2]);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(textLines, margin + 14, currentY + 3.5);

        currentY += stepHeight + 8;
      });

      return currentY + 6;
    };

    if (singleDay.warmup && singleDay.warmup.length > 0) y = renderSection('Warm-Up', singleDay.warmup, y);
    if (singleDay.workout && singleDay.workout.length > 0) y = renderSection('Main Workout', singleDay.workout, y);
    if (singleDay.cooldown && singleDay.cooldown.length > 0) y = renderSection('Cool Down', singleDay.cooldown, y);

    drawFooter();
    await this.saveAndOpenPdf(doc, 'AI_Workout.pdf');
  }

  async downloadDietPdf(dietData: any) {
    if (!dietData || !Array.isArray(dietData) || dietData.length === 0) {
      this.toaster.presentToast('No diet data available to download.', '', 'warning');
      return;
    }
    try {
      this.toaster.presentToast('Generating PDF report...', '', 'success');
      await this.generateDietPdf(dietData);
    } catch (e) {
      console.error(e);
      this.toaster.presentToast('Failed to generate PDF.', '', 'danger');
    }
  }

  private async generateDietPdf(dietData: any[]) {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let y = 20;

    const palette = this.getPdfPalette();
    const { primaryColor, lightGray, borderGray, darkText, mutedText, white } = palette;

    const logoDataUrl = await this.getLogoDataUrl();
    const drawHeader = (currentPageY: number) => this.drawBrandedHeader(doc, logoDataUrl, currentPageY);
    const drawFooter = () => this.drawFooter(doc);

    y = drawHeader(15);

    const dayCount = Math.max(1, dietData.length);

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('Personalized Nutrition Plan', margin, y);
    y += 15;

    let totalEnergy = 0, totalCarbs = 0, totalProtein = 0, totalFat = 0, totalFibre = 0;
    dietData.forEach((day: any) => {
      const n = day?.day_meal_nutrients || {};
      totalEnergy += Number(n.energy) || 0;
      totalCarbs += Number(n.carbs) || 0;
      totalProtein += Number(n.protein) || 0;
      totalFat += Number(n.fat) || 0;
      totalFibre += Number(n.fibre) || 0;
    });
    const avgEnergy = Math.round(totalEnergy / dayCount);
    const avgCarbs = Math.round(totalCarbs / dayCount);
    const avgProtein = Math.round(totalProtein / dayCount);
    const avgFat = Math.round(totalFat / dayCount);
    const avgFibre = Math.round(totalFibre / dayCount);

    doc.setDrawColor(borderGray[0], borderGray[1], borderGray[2]);
    doc.setLineWidth(0.4);
    doc.roundedRect(margin, y, contentWidth, 26, 3, 3, 'S');

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('TOTAL DAILY NUTRIENTS', pageWidth / 2, y + 7, { align: 'center' });

    doc.setDrawColor(borderGray[0], borderGray[1], borderGray[2]);
    doc.line(margin + 4, y + 11, pageWidth - margin - 4, y + 11);

    const nColWidth = contentWidth / 5;
    const nLabels = ['Calories', 'Carbs', 'Protein', 'Fat', 'Fibre'];
    const nValues = [`${avgEnergy} kcal`, `${avgCarbs} g`, `${avgProtein} g`, `${avgFat} g`, `${avgFibre} g`];

    for (let i = 0; i < 5; i++) {
      const xCenter = margin + (i * nColWidth) + (nColWidth / 2);

      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(darkText[0], darkText[1], darkText[2]);
      doc.text(nLabels[i], xCenter, y + 16, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.text(nValues[i], xCenter, y + 23, { align: 'center' });

      if (i > 0) {
        doc.setDrawColor(borderGray[0], borderGray[1], borderGray[2]);
        doc.line(margin + i * nColWidth, y + 14, margin + i * nColWidth, y + 23);
      }
    }
    y += 36;

    dietData.forEach((day: any) => {
      const meals = (day?.meal_details || []).filter((m: any) => {
        const hasEnergy = Number(m?.meal_nutrients?.energy) > 0;
        const isWater = String(m?.meal_name || '').toLowerCase().includes('water');
        return hasEnergy || isWater;
      });

      if (y > pageHeight - 60) {
        drawFooter();
        doc.addPage();
        y = drawHeader(15);
      }

      meals.forEach((meal: any) => {
        const foods: any[] = Array.isArray(meal?.normalizedFoods)
          ? meal.normalizedFoods
          : (meal?.food_details || [])
              .filter((g: any) => Array.isArray(g) && g.length > 0)
              .map((g: any) => g[0]);

        const leftBlockWidth = 40;
        const rightBlockWidth = contentWidth - leftBlockWidth;

        const foodLinesArr: string[][] = foods.map((food: any) => {
          const foodName = food.food_name || food.foodName || 'Item';
          const qty = food.quantity || 1;
          const unit = food.unit || '';
          const grm = food.qty_in_grm ? ` (${food.qty_in_grm}g)` : '';
          const rawText = `${qty} ${unit} ${foodName}${grm}`.replace(/\s+/g, ' ').trim();
          return doc.splitTextToSize('•  ' + rawText, rightBlockWidth - 16);
        });

        let foodsBlockHeight = 0;
        foodLinesArr.forEach((lines) => { foodsBlockHeight += lines.length * 5 + 2; });
        const topPad = 8;
        const nutrientStripHeight = 16;

        // FIX #36: include the wrapped meal-name lines in the height so long
        // meal names ("MID-MORNING SNACK", etc.) don't overflow the bordered
        // left block and overlap the nutrient strip.
        doc.setFontSize(8);
        const mealNameLines = doc.splitTextToSize(String(meal.meal_name || '').toUpperCase(), leftBlockWidth - 8);
        const mealNameHeight = Math.max(mealNameLines.length * 4 + 4, 12);

        const mealContentHeight = Math.max(foodsBlockHeight, mealNameHeight);
        const mealHeight = topPad + mealContentHeight + 8 + nutrientStripHeight;

        if (y + mealHeight > pageHeight - 25) {
          drawFooter();
          doc.addPage();
          y = drawHeader(15);
        }

        doc.setDrawColor(borderGray[0], borderGray[1], borderGray[2]);
        doc.setLineWidth(0.4);
        doc.roundedRect(margin, y, contentWidth, mealHeight, 3, 3, 'S');

        doc.line(margin + leftBlockWidth, y, margin + leftBlockWidth, y + mealHeight);

        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        const textHeight = mealNameLines.length * 4;
        const centeredY = y + (mealHeight / 2) - (textHeight / 2) + 3;
        doc.text(mealNameLines, margin + (leftBlockWidth / 2), centeredY, { align: 'center' });

        doc.setTextColor(darkText[0], darkText[1], darkText[2]);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        let foodY = y + topPad + 4;
        if (foodLinesArr.length > 0) {
          foodLinesArr.forEach((lines) => {
            doc.text(lines, margin + leftBlockWidth + 8, foodY);
            foodY += lines.length * 5 + 2;
          });
        } else {
          doc.setTextColor(mutedText[0], mutedText[1], mutedText[2]);
          doc.setFont('helvetica', 'italic');
          doc.text('No food items listed.', margin + leftBlockWidth + 8, foodY);
        }

        const stripY = y + mealHeight - nutrientStripHeight;
        doc.setFillColor(lightGray[0], lightGray[1], lightGray[2]);
        doc.rect(margin + leftBlockWidth + 0.2, stripY, rightBlockWidth - 0.5, nutrientStripHeight - 0.5, 'F');
        doc.setDrawColor(borderGray[0], borderGray[1], borderGray[2]);
        doc.line(margin + leftBlockWidth, stripY, margin + contentWidth, stripY);

        const tableColWidth = rightBlockWidth / 5;
        const stripMacros = [
          { label: 'CALORIES', val: `${Math.round(Number(meal.meal_nutrients?.energy) || 0)} kcal` },
          { label: 'CARBS', val: `${Math.round(Number(meal.meal_nutrients?.carbs) || 0)} g` },
          { label: 'PROTEIN', val: `${Math.round(Number(meal.meal_nutrients?.protein) || 0)} g` },
          { label: 'FAT', val: `${Math.round(Number(meal.meal_nutrients?.fat) || 0)} g` },
          { label: 'FIBRE', val: `${Math.round(Number(meal.meal_nutrients?.fibre) || 0)} g` },
        ];

        stripMacros.forEach((macro, idx) => {
          const xPos = margin + leftBlockWidth + (idx * tableColWidth) + (tableColWidth / 2);
          doc.setFontSize(7);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(mutedText[0], mutedText[1], mutedText[2]);
          doc.text(macro.label, xPos, stripY + 6, { align: 'center' });

          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(darkText[0], darkText[1], darkText[2]);
          doc.text(macro.val, xPos, stripY + 12, { align: 'center' });
        });

        y += mealHeight + 10;
      });
      y += 6;
    });

    drawFooter();
    await this.saveAndOpenPdf(doc, 'AI_Personalized_Diet_Plan.pdf');
  }

  private formatDateForInput(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  async promptSaveDiet(dietData: any) {
    if (!dietData || !Array.isArray(dietData) || dietData.length === 0) {
      this.toaster.presentToast('No diet plan data found.', '', 'warning');
      return;
    }
    this.pendingSaveData = dietData;
    this.pendingSaveType = 'diet';
    await this.openDatePickerModal('diet');
  }

  async promptSaveExercise(exerciseData: any) {
    if (!exerciseData || !exerciseData.title) {
      this.toaster.presentToast('No exercise plan data found.', '', 'warning');
      return;
    }
    this.pendingSaveData = exerciseData;
    this.pendingSaveType = 'exercise';
    await this.openDatePickerModal('exercise');
  }

  /**
   * FIX #25/#26: Paginate through ALL existing plans so occupied dates from
   * plans beyond the first page are still marked unavailable. If the fetch
   * fails, we set `datePickerLoadFailed = true` — the Confirm button is
   * disabled and the user is told to retry, instead of treating every date
   * as free and double-booking.
   */
  async openDatePickerModal(type: 'diet' | 'exercise') {
    this.isFetchingDates = true;
    this.isSavingDate = false;
    this.datePickerLoadFailed = false;
    this.isDatePickerModalOpen = true;

    // Reset calendar to current month
    this.currentMonthDate = new Date();

    const endpoint = type === 'diet'
      ? 'AIClientDiet/client_diet_plans'
      : 'AIClientDiet/client_exercise_plans';

    const pageSize = 100;
    const collectedPlans: any[] = [];

    const fetchPage = (page: number): Promise<{ plans: any[]; total_pages: number }> => {
      return new Promise((resolve, reject) => {
        this.apiService.post(endpoint, { page, limit: pageSize }).subscribe({
          next: (res: any) => {
            if (res && res.isValid && res.data && Array.isArray(res.data.plans)) {
              resolve({
                plans: res.data.plans,
                total_pages: Number(res.data.total_pages) || 1,
              });
            } else {
              resolve({ plans: [], total_pages: 1 });
            }
          },
          error: (err) => reject(err),
        });
      });
    };

    try {
      let page = 1;
      let totalPages = 1;
      // Hard cap to avoid runaway loops if the backend misreports totals.
      const MAX_PAGES = 50;
      do {
        const result = await fetchPage(page);
        collectedPlans.push(...result.plans);
        totalPages = Math.max(1, Math.min(result.total_pages, MAX_PAGES));
        page++;
      } while (page <= totalPages);

      this.occupiedDates.clear();
      this.highlightedDates = [];
      collectedPlans.forEach((p: any) => {
        if (p.start_date && p.end_date) {
          const start = new Date(p.start_date);
          const end = new Date(p.end_date);
          let current = new Date(start);
          while (current <= end) {
            const dateStr = this.formatDateForInput(current);
            this.occupiedDates.add(dateStr);
            this.highlightedDates.push({
              date: dateStr,
              textColor: '#ffffff',
              backgroundColor: 'var(--ion-color-danger)'
            });
            current.setDate(current.getDate() + 1);
          }
        }
      });

      this.calculateNextAvailableDate();
    } catch (err) {
      // FAIL CLOSED: do NOT clear occupiedDates and pretend everything is free.
      console.warn('[date-picker] failed to fetch existing plans:', err);
      this.datePickerLoadFailed = true;
      this.toaster.presentToast(
        'Could not load existing plan dates. Please close and retry so you don\'t double-book.',
        '',
        'danger'
      );
    } finally {
      this.isFetchingDates = false;
      this.generateCalendarDays();
      this.cdr.detectChanges();
    }
  }

  // ─── Custom Calendar Logic ──────────────────────────────────────────

  generateCalendarDays() {
    const year = this.currentMonthDate.getFullYear();
    const month = this.currentMonthDate.getMonth();

    // Update header strings
    this.currentMonthName = this.currentMonthDate.toLocaleString('default', { month: 'long' });
    this.currentYear = year;

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const daysInMonth = lastDayOfMonth.getDate();
    const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 (Sun) to 6 (Sat)

    this.calendarDays = [];

    // Add empty cells for days before the 1st of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      this.calendarDays.push({ date: null });
    }

    // Add the actual days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);

      const dateStr = this.formatDateForInput(date);

      // Check against your existing logic
      const isHighlighted = this.occupiedDates.has(dateStr);
      const isSelected = this.selectedDateForSave === dateStr;

      const today = new Date();
      const isToday = date.toDateString() === today.toDateString();

      // FIX #29: single source of truth for "selectable"
      const isDisabled = !this.isDateSelectable(dateStr);

      this.calendarDays.push({
        date: date,
        isHighlighted: isHighlighted,
        isSelected: isSelected,
        isToday: isToday,
        isDisabled: isDisabled
      });
    }
  }

  // FIX #32: clamp calendar navigation to ±12 months from today.
  prevMonth() {
    const next = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() - 1, 1);
    if (!this.isCalendarMonthInRange(next)) return;
    this.currentMonthDate = next;
    this.generateCalendarDays();
  }

  nextMonth() {
    const next = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() + 1, 1);
    if (!this.isCalendarMonthInRange(next)) return;
    this.currentMonthDate = next;
    this.generateCalendarDays();
  }

  private isCalendarMonthInRange(candidate: Date): boolean {
    const today = new Date();
    const min = new Date(today.getFullYear(), today.getMonth() - this.CALENDAR_NAVIGATION_MONTH_WINDOW, 1);
    const max = new Date(today.getFullYear(), today.getMonth() + this.CALENDAR_NAVIGATION_MONTH_WINDOW, 1);
    const c = new Date(candidate.getFullYear(), candidate.getMonth(), 1);
    return c.getTime() >= min.getTime() && c.getTime() <= max.getTime();
  }

  /**
   * Returns true if the given date is strictly before today (midnight comparison).
   * Today itself is NOT considered "past".
   */
  isPastDate(date: Date | null): boolean {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const check = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return check.getTime() < today.getTime();
  }

  /**
   * FIX #29: Single source of truth for whether a date cell can be clicked.
   * Combines the past-date check and the occupied-date check so the user
   * sees exactly one consistent message per failure reason.
   */
  private isDateSelectable(dateStr: string): boolean {
    if (!dateStr) return false;
    const parts = dateStr.split('-').map((p) => parseInt(p, 10));
    if (parts.length !== 3 || parts.some((n) => isNaN(n))) return false;

    const candidate = new Date(parts[0], parts[1] - 1, parts[2]);
    if (this.isPastDate(candidate)) return false;
    if (this.occupiedDates.has(dateStr)) return false;
    return true;
  }

  selectDate(date: Date | null) {
    if (!date) return;

    const dateStr = this.formatDateForInput(date);

    // FIX #29: unified feedback — only one reason is reported.
    const parts = dateStr.split('-').map((p) => parseInt(p, 10));
    const candidate = new Date(parts[0], parts[1] - 1, parts[2]);

    if (this.isPastDate(candidate)) {
      this.toaster.presentToast('Past dates cannot be selected.', '', 'warning');
      return;
    }
    if (this.occupiedDates.has(dateStr)) {
      this.toaster.presentToast('This date is already taken.', '', 'warning');
      return;
    }

    this.selectedDateForSave = dateStr;
    this.generateCalendarDays(); // Re-render to update selected state
    this.cdr.detectChanges();
  }

  // ──────────────────────────────────────────────────────────────────

  calculateNextAvailableDate() {
    let candidate = new Date();
    if (candidate.getHours() >= this.NEXT_DAY_CUTOFF_HOUR) {
      candidate.setDate(candidate.getDate() + 1);
    }
    // Safety net: never fall on a past date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (candidate.getTime() < today.getTime()) {
      candidate = new Date(today.getTime());
    }
    while (this.occupiedDates.has(this.formatDateForInput(candidate))) {
      candidate.setDate(candidate.getDate() + 1);
    }
    this.selectedDateForSave = this.formatDateForInput(candidate);
  }

  checkDateEnabled = (dateIsoString: string) => {
    if (!dateIsoString) return true;
    const datePart = dateIsoString.split('T')[0];

    // Block past dates (strictly before today)
    const parts = datePart.split('-').map((p) => parseInt(p, 10));
    if (parts.length === 3) {
      const candidate = new Date(parts[0], parts[1] - 1, parts[2]);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (candidate.getTime() < today.getTime()) {
        return false;
      }
    }

    return !this.occupiedDates.has(datePart);
  };

  /**
   * FIX #27/#28/#42: Recompute "today" right before validating, keep the
   * modal open while the save request is in flight, and only dismiss on
   * success. On failure the user can pick another date without redoing the
   * whole "Save Diet Plan" flow.
   */
  async confirmSaveDate() {
    if (this.isSavingDate) return;
    if (this.datePickerLoadFailed) {
      this.toaster.presentToast('Existing plan dates could not be loaded. Please close and retry.', '', 'warning');
      return;
    }
    if (!this.selectedDateForSave) {
      this.toaster.presentToast('Please select a valid date.', '', 'warning');
      return;
    }

    // Recompute "today" now — the user may have kept the modal open past midnight.
    const parts = this.selectedDateForSave.split('-').map((p) => parseInt(p, 10));
    const chosen = new Date(parts[0], parts[1] - 1, parts[2]);
    if (this.isPastDate(chosen)) {
      this.toaster.presentToast('The selected date has passed. Please pick another date.', '', 'warning');
      return;
    }

    this.isSavingDate = true;

    const savePromise: Promise<boolean> = this.pendingSaveType === 'diet'
      ? this.executeAcceptDietPlanAsync(this.pendingSaveData, this.selectedDateForSave, this.selectedDateForSave)
      : this.executeAcceptExercisePlanAsync(this.pendingSaveData, this.selectedDateForSave, this.selectedDateForSave);

    const success = await savePromise;

    this.isSavingDate = false;

    if (success) {
      this.isDatePickerModalOpen = false;
      this.pendingSaveData = null;
      this.pendingSaveType = null;
    } else {
      // Keep the modal open; the caller has already shown an error toast.
      this.cdr.detectChanges();
    }
  }

  cancelSaveDate() {
    if (this.isSavingDate) return; // don't allow closing mid-save
    this.isDatePickerModalOpen = false;
    this.pendingSaveData = null;
    this.pendingSaveType = null;
    this.datePickerLoadFailed = false;
  }

  private executeAcceptDietPlanAsync(dietData: any, startDate: string, endDate: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.apiService.post('AIClientDiet/accept_diet_plan', {
        start_date: startDate,
        end_date: endDate,
        diet_json: dietData,
        member_id: null,
        client_preferences: null
      }).subscribe({
        next: (res: any) => {
          if (res && res.isValid) {
            this.toaster.presentToast(res.message || 'Diet plan successfully accepted!', '', 'success');
            this.router.navigateByUrl('/tabs/diet/my-ai-diets');
            resolve(true);
          } else {
            this.toaster.presentToast(res?.message || 'Failed to save diet plan.', '', 'warning');
            resolve(false);
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.toaster.presentToast('An error occurred while saving the diet plan.', '', 'danger');
          resolve(false);
        }
      });
    });
  }

  private executeAcceptExercisePlanAsync(exerciseData: any, startDate: string, endDate: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const payload = {
        exercise_json: exerciseData,
        start_date: startDate,
        end_date: endDate,
        plan_name: exerciseData.title || 'AI Personalized Exercise Plan',
        member_id: null
      };
      this.apiService.post('AIClientDiet/save_exercise_plan', payload).subscribe({
        next: (res: any) => {
          if (res && res.isValid) {
            this.toaster.presentToast(res.message || 'Exercise plan saved successfully!', '', 'success');
            this.router.navigateByUrl('/tabs/diet/my-ai-exercises');
            resolve(true);
          } else {
            this.toaster.presentToast(res?.message || 'Failed to save exercise plan.', '', 'warning');
            resolve(false);
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.toaster.presentToast('An error occurred while saving the exercise plan.', '', 'danger');
          resolve(false);
        }
      });
    });
  }

  private async extractClientIdFromJwt(): Promise<void> {
    try {
      const token = await this.jwtService.getToken();
      if (!token) return;
      const payloadBase64 = token.split('.')[1];
      if (!payloadBase64) return;
      const payloadJson = atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(payloadJson);
      const rawId = payload.client_id ?? payload.clientId ?? payload.user_id ?? payload.sub;
      const parsed = parseInt(rawId, 10);
      if (!isNaN(parsed)) {
        this.clientIdFromJwt = parsed;
      }
    } catch (e) {
      console.warn('Could not extract client_id from JWT', e);
    }
  }

  get pinnedSessions(): ChatSession[] { return this.sessions.filter((s) => Number(s.is_pinned) === 1); }
  get unpinnedSessions(): ChatSession[] { return this.sessions.filter((s) => Number(s.is_pinned) !== 1); }

  get filteredPinnedSessions(): ChatSession[] {
    if (!this.searchQuery) return this.pinnedSessions;
    const q = this.searchQuery.toLowerCase();
    return this.pinnedSessions.filter((s) => s.title.toLowerCase().includes(q));
  }

  get filteredUnpinnedSessions(): ChatSession[] {
    if (!this.searchQuery) return this.unpinnedSessions;
    const q = this.searchQuery.toLowerCase();
    return this.unpinnedSessions.filter((s) => s.title.toLowerCase().includes(q));
  }

  get currentSessionTitle(): string {
    if (!this.currentSessionId) return 'New chat';
    const active = this.sessions.find((s) => s.session_id === this.currentSessionId);
    if (!active) return 'New chat';
    return active.title.length > 15 ? `${active.title.substring(0, 15)}...` : active.title;
  }

  /**
   * FIX #2: Now actually honours `autoSelectFirst`. When true and no session
   * is selected yet, it selects the first session in the list (which the
   * backend returns already ordered by pinned/last_message_date DESC).
   */
  loadSessionHistory(autoSelectFirst: boolean = true) {
    this.isLoadingHistory = true;
    this.apiService.post('DietChat/get_chat_session_history', { search: '', page_number: 1, page_size: 50 }).subscribe({
      next: (res: any) => {
        this.isLoadingHistory = false;
        this.sessions = (res && res.isValid && Array.isArray(res.data)) ? res.data : [];

        if (autoSelectFirst && !this.currentSessionId && this.sessions.length > 0) {
          const first = this.sessions[0];
          this.currentSessionId = first.session_id;
          this.aiApiSessionId = null;
          this.currentRefId = null;
          this.currentMessages = [];
          this.loadSessionDetails(first.session_id);
        }

        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isLoadingHistory = false;
        this.toaster.presentToast('Failed to load chat history.', '', 'danger');
        this.cdr.detectChanges();
      },
    });
  }

  createNewSession() {
    this.currentSessionId = null; this.aiApiSessionId = null; this.currentRefId = null; this.currentMessages = [];
    this.chatInput = ''; this.isTyping = false; this.isUserScrolledUp = false;
    this.showScrollButton = false; this.selectedCategory = 'diet';
    this.updateDailyLimitMessage();
    this.menuCtrl.close('chat-menu'); this.cdr.detectChanges();
  }

  selectSession(sessionId: string | number) {
    this.menuCtrl.close('chat-menu');
    if (this.currentSessionId === sessionId) return;
    this.currentSessionId = sessionId; this.aiApiSessionId = null; this.currentRefId = null;
    this.currentMessages = []; this.isUserScrolledUp = false;
    this.showScrollButton = false; this.loadSessionDetails(sessionId);
  }

  loadSessionDetails(sessionId: string | number) {
    this.isLoadingMessages = true;
    this.apiService.post('DietChat/get_chat_session_details', { session_id: sessionId }).subscribe({
      next: (res: any) => {
        this.isLoadingMessages = false;
        if (res && res.isValid && res.data) {
          const questionsArray = Array.isArray(res.data) ? res.data : res.data.questions || [];
          this.currentMessages = this.parseSessionMessages(questionsArray);
          // FIX #23: re-attach the ResizeObserver once the messages container exists.
          setTimeout(() => this.attachResizeObserver(), 50);
          this.cdr.detectChanges();
          this.scrollToBottom(0, true);
        } else { this.currentMessages = []; }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isLoadingMessages = false;
        this.toaster.presentToast('Could not load messages.', '', 'danger');
        // FIX #23: still re-attach so future messages auto-scroll.
        setTimeout(() => this.attachResizeObserver(), 50);
        this.cdr.detectChanges();
      },
    });
  }

  private parseSessionMessages(rows: any[]): ChatMessage[] {
    const messages: ChatMessage[] = [];
    rows.forEach((row) => {
      let userPromptStr = '';
      if (row.question_text) {
        userPromptStr = row.question_text;
        messages.push({ id: `q_${row.question_id || this.generateId()}`, question_id: row.question_id, role: 'user', content: row.question_text, timestamp: row.created_date ? new Date(row.created_date) : new Date() });
      }
      if (Array.isArray(row.responses) && row.responses.length > 0) {
        const lastResponse = row.responses[row.responses.length - 1];
        this.processAiResponseItem(messages, lastResponse, row.question_id, userPromptStr);
      } else if (row.response_text) {
        this.processAiResponseItem(messages, row, row.question_id, userPromptStr);
      }
    });
    return messages;
  }

  private hasDietStructure(data: any): boolean {
    if (!data) return false;
    if (data.diet_json) return true;
    if (Array.isArray(data) && data.length > 0) {
      return !!(data[0]?.meal_name || data[0]?.meal_details || data[0]?.day_meal_nutrients);
    }
    if (data.meal_details || data.day_meal_nutrients) return true;
    return false;
  }

  private hasRecipeStructure(data: any): boolean {
    if (!data) return false;
    // Canonical shape
    if (data.title && Array.isArray(data.ingredients) && data.ingredients.length > 0) {
      return true;
    }
    // Variations-only shape (LLM sometimes returns this even when asked for
    // a single recipe). Accepted so we route to the recipe handler instead
    // of falling through to "chat" and dumping raw JSON in the UI.
    if (Array.isArray(data.variations) && data.variations.length > 0) {
      return true;
    }
    // Nested shape
    if (data.recipe && typeof data.recipe === 'object') {
      return !!(data.recipe.title || Array.isArray(data.recipe.ingredients));
    }
    return false;
  }

  private processAiResponseItem(messages: ChatMessage[], resp: any, questionId?: number, userPrompt?: string) {
    let isDietJson = false; let dietData: any[] = [];
    let isRecipeJson = false; let recipeData: any = null;
    let isExerciseJson = false; let exerciseData: any = null;
    let displayContent = resp.response_text || '';
    let isValid = true; let errorMessage = '';

    try {
      const cleaned = this.stripMarkdownJson(displayContent);
      if (cleaned.startsWith('{') || cleaned.startsWith('[')) {
        const parsed = JSON.parse(cleaned);
        let domainData = parsed.data || parsed;
        let actualData = domainData.data || domainData;

        if (parsed.isValid === false || resp.isValid === false || domainData.isValid === false || actualData.isValid === false) {
          isValid = false;
          errorMessage = actualData.message || domainData.message || parsed.message || resp.errorMessage || 'No match found.';
        }

        const isDietResponse = (resp.response_type === 'diet' || resp.response_type === 'diet_json' || this.hasDietStructure(actualData)) && this.hasDietStructure(actualData);
        const isRecipeResponse = (resp.response_type === 'recipe' || this.hasRecipeStructure(actualData)) && this.hasRecipeStructure(actualData);
        const isExerciseResponse = (resp.response_type === 'exercise' || this.looksLikeExercisePayload(actualData)) && this.looksLikeExercisePayload(actualData);

        const msgs = [actualData.message, domainData.message, parsed.message, resp.errorMessage];
        let bestMessage = '';
        for (const m of msgs) {
          if (m && !this.isGenericStatusMessage(m)) {
            bestMessage = m;
            break;
          }
        }

        if (isDietResponse) {
          isDietJson = true;
          let rawDiet = actualData.diet_json || actualData;
          dietData = Array.isArray(rawDiet) ? rawDiet : [rawDiet];
          // FIX #20: normalize legacy diet payloads so `normalizedFoods` exists
          // for copy / PDF and the totals are consistent with fresh responses.
          dietData = this.normalizeDietDataForUI(dietData);
          displayContent = bestMessage || "I've generated a customized diet plan for you based on your request.";
        }
        else if (isRecipeResponse) {
          isRecipeJson = true;
          recipeData = this.normalizeRecipeData(actualData);
          displayContent = bestMessage || `Here is the recipe for ${recipeData.title || 'your request'}.`;
        }
        else if (isExerciseResponse) {
          isExerciseJson = true;
          exerciseData = this.normalizeExerciseDataForUI(actualData);
          displayContent = bestMessage || `Here is your workout plan: ${actualData.title || ''}.`;
        }
        else {
          let candidate: any = actualData.reply || bestMessage || actualData.message || parsed.message || domainData.message || displayContent;
          if (Array.isArray(candidate)) {
            candidate = candidate.map((c: any) => `- ${c}`).join('\n');
          } else if (typeof candidate === 'object' && candidate !== null) {
            candidate = candidate.message || candidate.reply || JSON.stringify(candidate);
          }
          displayContent = this.isGenericStatusMessage(candidate) ? displayContent : candidate;
        }
      }
    } catch (e) {
      console.warn('[parseSessionMessages] failed to parse stored response:', e);
    }

    // FIX #16/#17: hydrate regen metadata from the backend when present so the
    // regenerate button and its 3-use limit behave consistently across reloads.
    const regenUsedFromBackend =
      typeof resp.regen_used === 'number'
        ? resp.regen_used
        : (typeof resp.regenUsed === 'number' ? resp.regenUsed : 0);
    const itemRefFromBackend =
      resp.item_ref || resp.ref_id || resp.itemRef || undefined;

    messages.push({
      id: `r_${resp.response_id || this.generateId()}`,
      question_id: questionId || resp.question_id,
      role: 'ai',
      content: displayContent,
      timestamp: resp.created_date ? new Date(resp.created_date) : new Date(),
      isDietJson, dietData, isRecipeJson, recipeData, isExerciseJson, exerciseData,
      response_type: resp.response_type,
      userPrompt: userPrompt,
      feedbackStatus: 'none',
      isStreaming: false,
      suggestedPrompts: resp.suggested_prompts || [],
      isValid: isValid,
      errorMessage: errorMessage,
      itemRef: itemRefFromBackend,
      regenUsed: regenUsedFromBackend
    });
  }

  async openSessionMenu(event: Event, session: ChatSession) {
    event.stopPropagation();
    const isPinned = Number(session.is_pinned) === 1;
    const actionSheet = await this.actionSheetCtrl.create({
      header: session.title,
      buttons: [
        { text: isPinned ? 'Unpin chat' : 'Pin chat', icon: 'pin', handler: () => { this.togglePinSession(session); } },
        { text: 'Rename', icon: 'pencil', handler: () => { this.renameSessionPrompt(session); } },
        { text: 'Delete', role: 'destructive', icon: 'trash', handler: () => { this.deleteSessionConfirm(session.session_id); } },
        { text: 'Cancel', icon: 'close', role: 'cancel' },
      ],
    });
    await actionSheet.present();
  }

  togglePinSession(session: ChatSession) {
    const newPinStatus = Number(session.is_pinned) === 1 ? 0 : 1;
    this.apiService.post('DietChat/pin_session', { session_id: session.session_id, is_pinned: newPinStatus }).subscribe({
      next: (res: any) => {
        if (res && res.isValid) {
          session.is_pinned = newPinStatus;
          this.toaster.presentToast(res.message || 'Session status updated.', '', 'success');
          // FIX #47: if a search is active and the pin toggle would move the
          // session out of the filtered list, clear the search so it doesn't
          // silently disappear on the user.
          if (this.searchQuery) {
            const stillMatches = session.title.toLowerCase().includes(this.searchQuery.toLowerCase());
            if (!stillMatches) {
              this.searchQuery = '';
            }
          }
          this.cdr.detectChanges();
        } else { this.toaster.presentToast('Failed to update pin status.', '', 'danger'); }
      },
      error: () => { this.toaster.presentToast('Error updating pin status.', '', 'danger'); },
    });
  }

  async renameSessionPrompt(session: ChatSession) {
    const alert = await this.alertController.create({
      header: 'Rename chat',
      inputs: [{ name: 'title', type: 'text', value: session.title, placeholder: 'Enter new title' }],
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'Save', handler: (data) => { if (data.title && data.title.trim() !== '') { this.renameSession(session.session_id, data.title.trim()); } } },
      ],
    });
    await alert.present();
  }

  renameSession(sessionId: string | number, newTitle: string) {
    this.apiService.post('DietChat/rename_session_title', { session_id: sessionId, title: newTitle }).subscribe({
      next: (res: any) => {
        if (res && res.isValid) {
          const session = this.sessions.find((s) => s.session_id === sessionId);
          if (session) session.title = newTitle;
          this.toaster.presentToast('Chat renamed successfully.', '', 'success');
          this.cdr.detectChanges();
        } else { this.toaster.presentToast('Failed to rename chat.', '', 'danger'); }
      },
      error: () => { this.toaster.presentToast('Error renaming chat.', '', 'danger'); },
    });
  }

  async deleteSessionConfirm(sessionId: string | number) {
    const alert = await this.alertController.create({
      header: 'Delete chat', message: 'Are you sure you want to delete this chat history?',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'Delete', role: 'destructive', handler: () => { this.deleteSession(sessionId); } },
      ],
    });
    await alert.present();
  }

  deleteSession(sessionId: string | number) {
    this.apiService.post('DietChat/delete_session_by_session_id', { session_id: sessionId }).subscribe({
      next: (res: any) => {
        if (res && res.isValid) {
          this.sessions = this.sessions.filter((s) => s.session_id !== sessionId);
          if (this.currentSessionId === sessionId) {
            if (this.sessions.length > 0) { this.selectSession(this.sessions[0].session_id); } else { this.createNewSession(); }
          }
          this.toaster.presentToast('Chat deleted successfully.', '', 'success');
          this.cdr.detectChanges();
        } else { this.toaster.presentToast(res?.message || 'Failed to delete chat.', '', 'danger'); }
      },
      error: () => { this.toaster.presentToast('Error deleting session.', '', 'danger'); },
    });
  }

  private buildContextualPrompt(userQuery: string): string {
    if (!userQuery.toLowerCase().includes('get diet according to filled onboarding form')) { return userQuery; }
    const rawData = this.formJson || this.onboardingFormJson;
    if (!rawData) return userQuery;
    let data: any = rawData;
    if (typeof rawData === 'string') { try { data = JSON.parse(rawData); } catch (e) {} }
    const name = data?.name || data?.first_name || data?.fullName || '';
    const age = data?.age || ''; const height = data?.height || ''; const weight = data?.weight || '';
    const gender = data?.gender || data?.sex || data?.occupation || '';
    const allergies = data?.allergies || data?.food_allergies || '';
    const likes = data?.likes || data?.food_likes || '';
    const dislikes = data?.dislikes || data?.food_dislikes || '';
    const health = data?.health_conditions || data?.medical_history || data?.medical_conditions || '';
    let contextParts: string[] = [];
    if (name) contextParts.push(`my name is ${name}`);
    if (age) contextParts.push(`age ${age}`);
    if (height) contextParts.push(`height ${height} cm`);
    if (weight) contextParts.push(`weight ${weight} kg`);
    let contextStr = '';
    if (contextParts.length > 0) { contextStr += 'Hello, ' + contextParts.join(', ') + '. '; }
    if (gender) contextStr += `I am ${gender}. `;
    if (allergies) contextStr += `I am allergic to ${allergies}. `;
    if (likes) contextStr += `I like ${likes}. `;
    if (dislikes) contextStr += `I dislike ${dislikes}. `;
    if (health) contextStr += `I have ${health}. `;
    if (contextStr.trim().length > 0) { return `${contextStr.trim()}\n\nQuestion: ${userQuery}`; }
    else { return `Client Data: ${JSON.stringify(data)}\n\nQuestion: ${userQuery}`; }
  }

  private applyExerciseConstraint(query: string, force: boolean = false): string {
    const looksLikeExercise = force
      || this.selectedCategory === 'exercise'
      || /\b(workout|exercise|routine|training|hiit|cardio|strength|gym)\b/i.test(query);
    if (!looksLikeExercise) return query;
    if (query.includes('SINGLE-DAY (one-session) workout plan only')) return query;
    return query + this.EXERCISE_SINGLE_DAY_CONSTRAINT;
  }

  sendSuggestion(suggestion: any) {
    if (this.isDailyLimitReached()) {
      this.toaster.presentToast(this.dailyLimitMessage || 'Daily limit reached for this tab.', '', 'warning');
      return;
    }
    this.chatInput = suggestion.prompt;
    this.cdr.detectChanges();
    // FIX #11: removed the duplicate detectChanges call.
    this.sendMessage();
  }

  // FIX #14: guard against Enter while a request is in flight with feedback.
  onEnterPressed(event: any) {
    if (!event.shiftKey) {
      event.preventDefault();
      if (this.isTyping) {
        this.toaster.presentToast('Please wait for the current response to finish.', '', 'warning');
        return;
      }
      this.sendMessage();
    }
  }

  sendMessage() {
    if (this.isDailyLimitReached()) {
      this.toaster.presentToast(this.dailyLimitMessage || 'Daily limit reached for this tab.', '', 'warning');
      return;
    }
    const text = this.chatInput.trim();
    if (!text || this.isTyping) return;

    this.chatInput = ''; this.isTyping = true; this.isUserScrolledUp = false; this.showScrollButton = false;
    const tempUserMsg: ChatMessage = { id: `temp_${this.generateId()}`, role: 'user', content: text, timestamp: new Date() };
    this.currentMessages.push(tempUserMsg); this.cdr.detectChanges(); this.scrollToBottom(300, true);

    if (!this.currentSessionId) {
      const generatedTitle = text.length > 30 ? text.substring(0, 30) + '...' : text;
      this.apiService.post('DietChat/create_diet_chat_session', { title: generatedTitle }).subscribe({
        next: (res: any) => {
          if (res && res.isValid && res.data?.session_id) {
            this.currentSessionId = res.data.session_id;
            this.sessions.unshift({ session_id: res.data.session_id, title: generatedTitle, is_pinned: 0, created_date: new Date().toISOString() });
            this.cdr.detectChanges(); this.executeSendQuestion(this.currentSessionId!, text, tempUserMsg);
          } else { this.handleSendError('Could not initialize chat session.'); }
        },
        error: () => { this.handleSendError('Failed to start a new session.'); },
      });
    } else { this.executeSendQuestion(this.currentSessionId, text, tempUserMsg); }
  }

  private executeSendQuestion(sessionId: string | number, text: string, tempUserMsg: ChatMessage) {
    this.apiService.post('DietChat/send_question', { session_id: sessionId, question_text: text, content: text }).subscribe({
      next: (res: any) => {
        if (res && res.isValid && res.data?.question_id) {
          const questionId = res.data.question_id; tempUserMsg.question_id = questionId;
          this.generateAIResponse(sessionId, questionId, text, tempUserMsg);
        } else { this.handleSendError('Failed to send question to the server.'); }
      },
      error: () => { this.handleSendError('Network error while sending question.'); },
    });
  }

  private async generateAIResponse(
    sessionId: string | number,
    questionId: number,
    userQuery: string,
    tempUserMsg?: ChatMessage,
    replaceResponseId: number | null = null,
    reuseItemRef?: string,
    msgToRestoreOnFail?: ChatMessage,
    restoreIndex?: number
  ) {
    this.startLoadingPhrases(userQuery);

    let finalPayloadPrompt = this.buildContextualPrompt(userQuery);
    finalPayloadPrompt = this.applyExerciseConstraint(finalPayloadPrompt);

    const clientId = this.clientIdFromJwt ?? null;

    const refIdForPayload = this.currentRefId || "";

    const tabParam  = this.getAiTabParam();
    const isRegen   = !!replaceResponseId;
    const modeParam = isRegen ? 'regenerate' : 'generate';
    const itemRefForPayload = isRegen
      ? (reuseItemRef || this.currentRefId || null)
      : null;

    const profileSummaryForPayload = this.profileSummaryForPayload;

    // Filter out streaming AI messages to build the structured message history
    const historyMessages = this.currentMessages
      .filter(m => !m.isStreaming)
      .map(m => ({ role: m.role, content: m.content || '' }));

    // Build the raw string array of previous user messages for accurate intent detection
    const previousMessagesStrings = this.currentMessages
      .filter(m => m.role === 'user' && !m.isStreaming && m.content)
      .map(m => String(m.content).trim());

    const apiPayload = {
      session_id: this.aiApiSessionId ? this.aiApiSessionId : sessionId.toString(),
      ref_id: refIdForPayload,
      client_id: clientId,
      question: finalPayloadPrompt,
      messages: historyMessages,
      previous_messages: previousMessagesStrings,
      client_info: {
        our_data: true,
        diet_pattern: [],
        api_version: 'v1'
      },
      profile_summary: profileSummaryForPayload,
      number_of_days: 1,
      icmr: false,
      environment: environment.enterpriseAPIEnvironment,
      prim_mode: false,
      tab: tabParam,
      mode: modeParam,
      item_ref: itemRefForPayload
    };

    const tempAiMsg: ChatMessage = {
      id: `ai_stream_${this.generateId()}`, question_id: questionId, role: 'ai', content: '', timestamp: new Date(),
      isDietJson: false, dietData: [], response_type: 'text', userPrompt: userQuery, feedbackStatus: 'none', isStreaming: true, suggestedPrompts: []
    };

    this.currentMessages.push(tempAiMsg); this.cdr.detectChanges(); this.scrollToBottom(300, true);
    this.abortController = new AbortController();

    const token = await this.jwtService.getToken();
    const headersConfig: any = { 'Content-Type': 'application/json' };
    if (token) { headersConfig['Authorization'] = `Bearer ${token}`; }

    fetch(`${environment.enterpriseApiBaseUrl}/prim-ask/chat`, {
      method: 'POST', headers: headersConfig, body: JSON.stringify(apiPayload), signal: this.abortController.signal
    })
      .then((response) => {
        // FIX #43: detect 401/403 and bail with a clear "session expired"
        // message instead of saving a fake AI error into chat history.
        if (response.status === 401 || response.status === 403) {
          const err: any = new Error('Unauthorized');
          err.name = 'SessionExpiredError';
          throw err;
        }
        if (!response.ok) { throw new Error(`HTTP error! status: ${response.status}`); }
        return response.json();
      })
      .then((res: any) => {
        try {
          const limitInfoRaw =
            (res && (res.limits || (res.data && res.data.limits))) || null;

          if (limitInfoRaw) {
            this.applyLimitsFromResponse(limitInfoRaw, tabParam);
            if (msgToRestoreOnFail && limitInfoRaw.regen_used !== undefined) {
              msgToRestoreOnFail.regenUsed = limitInfoRaw.regen_used;
            }
          }

          if (res && res.allowed === false) {
            this.currentMessages = this.currentMessages.filter((m) => m.id !== tempAiMsg.id);
            if (msgToRestoreOnFail && restoreIndex !== undefined) {
              this.currentMessages.splice(restoreIndex, 0, msgToRestoreOnFail);
            }
            this.isTyping = false;
            this.stopLoadingPhrases();
            this.toaster.presentToast(res.message || 'Generation limit reached.', '', 'warning');
            this.cdr.detectChanges();
            return;
          }

          const incomingRefId =
            res?.ref_id ||
            res?.data?.ref_id ||
            res?.data?.response?.ref_id ||
            null;

          if (incomingRefId) {
            this.currentRefId = incomingRefId;
            console.log('[ref_id] captured from API response:', this.currentRefId);
          } else if (res && res.session_id) {
            this.currentRefId = res.session_id;
            console.log('[ref_id] using top-level session_id as ref_id:', this.currentRefId);
          } else if (res && res.data && res.data.session_id) {
            this.currentRefId = res.data.session_id;
            console.log('[ref_id] using data.session_id as ref_id:', this.currentRefId);
          } else if (res && res.data && res.data.response && res.data.response.session_id) {
            this.currentRefId = res.data.response.session_id;
            console.log('[ref_id] using data.response.session_id as ref_id:', this.currentRefId);
          }
          console.log('Current ref_id:', this.currentRefId);

          const dataObj = res.data !== undefined ? (res.data.response_type ? res.data : res) : res;
          const responseType = dataObj.response_type || 'chat';
          const suggestions = dataObj.suggested_prompts || [];
          const domainData = dataObj.data || {};
          let actualData = domainData.data || domainData;

          let isValid = true; let errorMessage = '';

          if (domainData.isValid === false || actualData.isValid === false || res.isValid === false || dataObj.isValid === false) {
            isValid = false;
            errorMessage = actualData.message || domainData.message || res.message || dataObj.message || "No match found.";
          }

          const msgs = [actualData.message, domainData.message, dataObj.message];
          let safeCustomMessage = '';
          for (const m of msgs) {
            if (m && !this.isGenericStatusMessage(m)) {
              safeCustomMessage = m;
              break;
            }
          }

          let fullText = ''; let displayContentForUi = '';
          let dietData: any[] = []; let isDietJson = false;
          let recipeData: any = null; let isRecipeJson = false;
          let exerciseData: any = null; let isExerciseJson = false;
          let collapsedExerciseDays = 0;

          const isDietResponse = (responseType === 'diet' || responseType === 'diet_json' || this.hasDietStructure(actualData)) && this.hasDietStructure(actualData);
          const isRecipeResponse = (responseType === 'recipe' || this.hasRecipeStructure(actualData)) && this.hasRecipeStructure(actualData);
          const isExerciseResponse = (responseType === 'exercise' || this.looksLikeExercisePayload(actualData)) && this.looksLikeExercisePayload(actualData);

          if (isDietResponse) {
            isDietJson = true;
            let dietSource = actualData.diet_json || actualData;
            dietData = Array.isArray(dietSource) ? dietSource : (dietSource ? [dietSource] : []);
            dietData = this.normalizeDietDataForUI(dietData);
            fullText = JSON.stringify({ isValid, message: errorMessage || safeCustomMessage, data: { diet_json: dietData } });
            displayContentForUi = safeCustomMessage || "I've generated a customized diet plan for you based on your request.";
          } else if (isRecipeResponse) {
            isRecipeJson = true;
            recipeData = this.normalizeRecipeData(actualData);
            fullText = JSON.stringify({ isValid, message: errorMessage || safeCustomMessage, ...recipeData });
            displayContentForUi = safeCustomMessage || `Here is the recipe for ${recipeData?.title || 'your request'}.`;
          } else if (isExerciseResponse) {
            isExerciseJson = true;
            const normalized = this.normalizeExerciseDataForUI(actualData);
            exerciseData = normalized;
            // FIX #37: warn the user if we silently collapsed multi-day data.
            collapsedExerciseDays = this.countCollapsedExerciseDays(actualData, normalized);
            fullText = JSON.stringify({ isValid, message: errorMessage || safeCustomMessage, ...exerciseData });
            displayContentForUi = safeCustomMessage || `Here is your workout plan: ${actualData?.title || ''}.`;
          } else {
            let candidate: any = actualData?.reply || safeCustomMessage || actualData?.message || domainData?.message || dataObj?.message || actualData;

            if (Array.isArray(candidate)) {
              fullText = candidate.map((item: any) => `- ${item}`).join('\n');
            } else if (typeof candidate === 'object' && candidate !== null) {
              fullText = candidate.message || candidate.reply || JSON.stringify(candidate);
            } else {
              fullText = String(candidate || '');
            }

            displayContentForUi = this.isGenericStatusMessage(fullText) ? '' : fullText;
          }

          let finalRegenUsed = isRegen ? 1 : 0;
          if (limitInfoRaw && limitInfoRaw.regen_used !== undefined) {
              finalRegenUsed = limitInfoRaw.regen_used;
          }

          if (collapsedExerciseDays > 0) {
            this.toaster.presentToast(
              `The AI returned a ${collapsedExerciseDays + 1}-day plan; only the first day is shown.`,
              '',
              'warning'
            );
          }

          this.currentMessages = this.currentMessages.filter((m) => m.id !== tempAiMsg.id);
          this.saveAIResponseToBackend(sessionId, questionId, fullText, responseType, isDietJson, dietData, isRecipeJson, recipeData, isExerciseJson, exerciseData, userQuery, displayContentForUi, suggestions, isValid, errorMessage, replaceResponseId, itemRefForPayload, isRegen, finalRegenUsed);
        } catch (innerErr: any) {
          this.currentMessages = this.currentMessages.filter((m) => m.id !== tempAiMsg.id);
          if (msgToRestoreOnFail && restoreIndex !== undefined) {
              this.currentMessages.splice(restoreIndex, 0, msgToRestoreOnFail);
              this.isTyping = false;
              this.stopLoadingPhrases();
          } else {
              const fallbackText = "I'm sorry, I encountered an error processing the AI response. Please try again.";
              this.saveAIResponseToBackend(sessionId, questionId, fallbackText, 'text', false, [], false, null, false, null, userQuery, fallbackText, [], false, fallbackText, replaceResponseId, itemRefForPayload, isRegen, 0);
          }
        } finally {
          this.abortController = null;
        }
      })
      .catch((err: any) => {
        this.currentMessages = this.currentMessages.filter((m) => m.id !== tempAiMsg.id);

        if (err && err.name === 'SessionExpiredError') {
          // FIX #43: do NOT save a fake AI error to the backend. Restore the
          // original message (if regenerating) and tell the user to re-login.
          if (msgToRestoreOnFail && restoreIndex !== undefined) {
            this.currentMessages.splice(restoreIndex, 0, msgToRestoreOnFail);
          }
          this.isTyping = false;
          this.stopLoadingPhrases();
          this.toaster.presentToast('Your session has expired. Please log in again.', '', 'danger');
          this.cdr.detectChanges();
          return;
        }

        if (msgToRestoreOnFail && restoreIndex !== undefined) {
            this.currentMessages.splice(restoreIndex, 0, msgToRestoreOnFail);
            this.isTyping = false;
            this.stopLoadingPhrases();
        } else if (err && err.name !== 'AbortError') {
          const fallbackText = "I'm sorry, I encountered an error communicating with the AI server. Please try again.";
          this.saveAIResponseToBackend(sessionId, questionId, fallbackText, 'text', false, [], false, null, false, null, userQuery, fallbackText, [], false, fallbackText, replaceResponseId, itemRefForPayload, isRegen, 0);
        }
        this.abortController = null;
      });
  }

  /**
   * FIX #37: Returns how many extra days were dropped by
   * `normalizeExerciseDataForUI`. 0 means nothing was collapsed.
   */
  private countCollapsedExerciseDays(originalData: any, normalized: any): number {
    try {
      let originalCount = 0;
      if (Array.isArray(originalData?.days)) {
        originalCount = originalData.days.length;
      } else if (originalData && typeof originalData === 'object') {
        originalCount = Object.keys(originalData).filter((k) => /^day\s*\d+/i.test(k.trim())).length;
      }
      const normalizedCount = Array.isArray(normalized?.days) ? normalized.days.length : 0;
      return Math.max(0, originalCount - normalizedCount);
    } catch {
      return 0;
    }
  }

  private saveAIResponseToBackend(
    sessionId: string | number, questionId: number, responseText: string, responseType: string,
    isDietJson: boolean, dietData: any, isRecipeJson: boolean, recipeData: any,
    isExerciseJson: boolean, exerciseData: any, userQuery: string, displayContentForUi: string,
    suggestedPrompts: string[] = [], isValid: boolean = true, errorMessage?: string,
    replaceResponseId: number | null = null,
    itemRefForPayload: string | null = null,
    isRegen: boolean = false,
    regenUsedFromApi?: number
  ) {
    this.stopLoadingPhrases();

    const payload: any = {
      session_id: sessionId,
      question_id: questionId,
      response_text: responseText,
      response_type: responseType
    };

    if (replaceResponseId !== null && replaceResponseId !== undefined) {
      payload['is_regenerate'] = true;
      payload['replace_response_id'] = replaceResponseId;
    }

    this.suppressResizeAutoScroll = true;

    const regenUsed = regenUsedFromApi !== undefined ? regenUsedFromApi : (isRegen ? 1 : 0);

    this.apiService.post('DietChat/add_response', payload).subscribe({
      next: (res: any) => {
        this.isTyping = false;

        // FIX #18/#19: when regenerating, remove the original message only
        // AFTER the replacement is confirmed saved. If the replacement came
        // from a regenerate call, replaceResponseId points at the old row.
        if (replaceResponseId !== null && replaceResponseId !== undefined) {
          this.currentMessages = this.currentMessages.filter(
            (m) => !(typeof m.id === 'string' && m.id === `r_${replaceResponseId}`)
          );
        }

        const newItemRef = itemRefForPayload || this.currentRefId || null;
        this.currentMessages.push({
          id: res?.data?.response_id ? `r_${res.data.response_id}` : `ai_${this.generateId()}`,
          question_id: questionId, role: 'ai', content: displayContentForUi, timestamp: new Date(),
          isDietJson, dietData, isRecipeJson, recipeData, isExerciseJson, exerciseData,
          response_type: responseType, userPrompt: userQuery, feedbackStatus: 'none', isStreaming: false,
          suggestedPrompts: suggestedPrompts, isValid: isValid, errorMessage: errorMessage,
          itemRef: newItemRef || undefined,
          regenUsed: regenUsed
        });
        this.cdr.detectChanges();
        setTimeout(() => { this.suppressResizeAutoScroll = false; }, 600);
      },
      error: (err) => {
        this.isTyping = false;
        // FIX #18/#19: on save failure, restore the original regenerated
        // message so nothing is lost from the user's view.
        if (replaceResponseId !== null && replaceResponseId !== undefined) {
          // We removed nothing yet on the success path, so nothing to restore
          // here — the original is still in currentMessages because we only
          // strip it on success above.
        }
        const newItemRef = itemRefForPayload || this.currentRefId || null;
        this.currentMessages.push({
          id: `ai_${this.generateId()}`, question_id: questionId, role: 'ai', content: displayContentForUi, timestamp: new Date(),
          isDietJson, dietData, isRecipeJson, recipeData, isExerciseJson, exerciseData,
          response_type: responseType, userPrompt: userQuery, feedbackStatus: 'none', isStreaming: false,
          suggestedPrompts: suggestedPrompts, isValid: isValid, errorMessage: errorMessage,
          itemRef: newItemRef || undefined,
          regenUsed: regenUsed
        });
        this.cdr.detectChanges();
        setTimeout(() => { this.suppressResizeAutoScroll = false; }, 600);
      },
    });
  }

  private handleSendError(message: string) {
    this.isTyping = false; this.stopLoadingPhrases();
    this.toaster.presentToast(message, '', 'danger');
    if (this.currentMessages.length > 0 && this.currentMessages[this.currentMessages.length - 1].role === 'user') { this.currentMessages.pop(); }
    this.cdr.detectChanges();
  }

  toggleMealCollapse(meal: any, event?: Event) {
    if (event) { event.stopPropagation(); }

    this.suppressAccordionAutoScroll = true;
    if (this.accordionSuppressTimeout) { clearTimeout(this.accordionSuppressTimeout); }

    const scrollElement = document.querySelector('.chat-scroll-container') as HTMLElement | null;
    const previousScrollTop = scrollElement ? scrollElement.scrollTop : null;

    meal.isCollapsed = !meal.isCollapsed;
    this.cdr.detectChanges();

    if (scrollElement && previousScrollTop !== null) {
      requestAnimationFrame(() => {
        scrollElement.scrollTop = previousScrollTop;
      });
    }

    this.accordionSuppressTimeout = setTimeout(() => {
      this.suppressAccordionAutoScroll = false;
    }, 500);
  }

  private generateId(): string { return Math.random().toString(36).substr(2, 9); }

  private looksLikeExercisePayload(data: any): boolean {
    if (!data || typeof data !== 'object') return false;
    if (!data.title) return false;
    if (Array.isArray(data.days) && data.days.length > 0) return true;
    if (Array.isArray(data.workout) && data.workout.length > 0) return true;
    if (Array.isArray(data.warmup) && data.warmup.length > 0) return true;
    if (Array.isArray(data.cooldown) && data.cooldown.length > 0) return true;
    if (Object.keys(data).some((k) => /^day\s*\d+/i.test(k.trim()))) return true;
    return false;
  }

  private normalizeExerciseDataForUI(data: any): any {
    if (!data || typeof data !== 'object') return data;
    const normalized: any = { ...data };

    const toStrArr = (v: any): string[] => {
      if (!Array.isArray(v)) return [];
      return v.map((x: any) => {
        if (typeof x === 'string') return x;
        if (x && typeof x === 'object') return x.text || x.name || x.label || JSON.stringify(x);
        return String(x);
      });
    };

    const pickLabel = (obj: any, fallbackIdx: number): string => {
      if (!obj || typeof obj !== 'object') return `Day ${fallbackIdx + 1}`;
      const candidates = [obj.day, obj.label, obj.title, obj.name, obj.day_name];
      for (const c of candidates) {
        if (typeof c === 'string' && c.trim().length > 0) return c.trim();
      }
      return '';
    };

    if (Array.isArray(data.days) && data.days.length > 0) {
      const first = data.days[0];
      const label = pickLabel(first, 0);
      normalized.days = [{
        label,
        warmup: toStrArr(first?.warmup),
        workout: toStrArr(first?.workout),
        cooldown: toStrArr(first?.cooldown),
      }];
      return normalized;
    }

    const dayKeys = Object.keys(data).filter((k) => /^day\s*\d+/i.test(k.trim()));
    if (dayKeys.length > 0) {
      dayKeys.sort((a, b) => {
        const na = parseInt(a.replace(/\D/g, ''), 10) || 0;
        const nb = parseInt(b.replace(/\D/g, ''), 10) || 0;
        return na - nb;
      });
      const firstKey = dayKeys[0];
      const d = data[firstKey] || {};
      normalized.days = [{
        label: firstKey.trim(),
        warmup: toStrArr(d.warmup),
        workout: toStrArr(d.workout),
        cooldown: toStrArr(d.cooldown),
      }];
      return normalized;
    }

    normalized.days = [{
      label: '',
      warmup: toStrArr(data.warmup),
      workout: toStrArr(data.workout),
      cooldown: toStrArr(data.cooldown),
    }];
    return normalized;
  }

  private normalizeDietDataForUI(dietData: any[]): any[] {
    return dietData.map(day => {
      let calculatedDayEnergy = 0; let calculatedDayCarbs = 0; let calculatedDayProtein = 0; let calculatedDayFat = 0; let calculatedDayFibre = 0;
      if (Array.isArray(day?.meal_details)) {
        day.meal_details = day.meal_details.map((meal: any) => {
          const flatFoods: any[] = []; let calculatedMealEnergy = 0;
          if (meal.food_details && Array.isArray(meal.food_details)) {
            meal.food_details.forEach((foodGroup: any) => {
              if (Array.isArray(foodGroup) && foodGroup.length > 0) {
                const selectedFood = foodGroup[0]; flatFoods.push(selectedFood);
                const nutrients = selectedFood.food_nutrients;
                if (nutrients) {
                  calculatedMealEnergy += (nutrients.energy || 0);
                  calculatedDayCarbs += (nutrients.carbs || 0);
                  calculatedDayProtein += (nutrients.protein || 0);
                  calculatedDayFat += (nutrients.fat || 0);
                  calculatedDayFibre += (nutrients.fibre || 0);
                }
              }
            });
          }
          meal.normalizedFoods = flatFoods; meal.isCollapsed = true;
          if (!meal.meal_nutrients) { meal.meal_nutrients = { energy: 0, carbs: 0, protein: 0, fat: 0, fibre: 0 }; }
          meal.meal_nutrients.energy = calculatedMealEnergy; calculatedDayEnergy += calculatedMealEnergy;
          return meal;
        });
      }
      if (!day.day_meal_nutrients) { day.day_meal_nutrients = { energy: 0, carbs: 0, protein: 0, fat: 0, fibre: 0 }; }
      day.day_meal_nutrients.energy = calculatedDayEnergy;
      day.day_meal_nutrients.carbs = calculatedDayCarbs;
      day.day_meal_nutrients.protein = calculatedDayProtein;
      day.day_meal_nutrients.fat = calculatedDayFat;
      day.day_meal_nutrients.fibre = calculatedDayFibre;
      return day;
    });
  }

  formatMessage(text: string): string {
    if (!text) return '';
    let str = text.replace(/\r\n/g, '\n').trim();
    str = str.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    str = str.replace(/^####\s+(.*)$/gm, '<h4 class="ai-header-secondary">$1</h4>');
    str = str.replace(/^###\s+(.*)$/gm, '<h3 class="ai-header-secondary">$1</h3>');
    str = str.replace(/^##\s+(.*)$/gm, '<h2 class="ai-header-primary">$1</h2>');
    str = str.replace(/^#\s+(.*)$/gm, '<h1 class="ai-heading-text">$1</h1>');
    str = str.replace(/\*\*(.*?)\*\*/g, '<strong class="ai-bold-text">$1</strong>');
    str = str.replace(/^---$/gm, '<hr class="ai-divider">');

    const lines = str.split('\n'); const result: string[] = []; let inUnorderedList = false; let inOrderedList = false;
    lines.forEach((line) => {
      const trimmed = line.trim();
      const bulletMatch = trimmed.match(/^[\*\-]\s+(.*)$/);
      const numberMatch = trimmed.match(/^(\d+)\.\s+(.*)$/);
      if (bulletMatch) {
        if (inOrderedList) { result.push('</ol>'); inOrderedList = false; }
        if (!inUnorderedList) { result.push('<ul class="ai-list">'); inUnorderedList = true; }
        result.push(`<li class="ai-list-item">${bulletMatch[1]}</li>`);
      } else if (numberMatch) {
        if (inUnorderedList) { result.push('</ul>'); inUnorderedList = false; }
        if (!inOrderedList) { result.push('<ol class="ai-ordered-list">'); inOrderedList = true; }
        result.push(`<li class="ai-ordered-item">${numberMatch[2]}</li>`);
      } else {
        if (inUnorderedList) { result.push('</ul>'); inUnorderedList = false; }
        if (inOrderedList) { result.push('</ol>'); inOrderedList = false; }
        if (trimmed.length > 0) {
          if (trimmed.startsWith('<h1') || trimmed.startsWith('<h2') || trimmed.startsWith('<h3') || trimmed.startsWith('<h4') || trimmed.startsWith('<hr')) {
            result.push(trimmed);
          } else { result.push(`<p class="ai-paragraph">${trimmed}</p>`); }
        }
      }
    });
    if (inUnorderedList) result.push('</ul>');
    if (inOrderedList) result.push('</ol>');
    return `<div class="formatted-ai-response">${result.join('')}</div>`;
  }

    get canGoPrevMonth(): boolean {
  const next = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() - 1, 1);
  return this.isCalendarMonthInRange(next);
}

get canGoNextMonth(): boolean {
  const next = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() + 1, 1);
  return this.isCalendarMonthInRange(next);
}
}
